import { DetailIcon, DetailModalControls } from './DetailModalControls';
import { useEmployeeDetailFilters } from './EmployeeDetailFilters';
import { sumRunRates, runRateSplit } from './gaRunRates';
import GARecordDetails from './GARecordDetails';
import { filterRecords, monthInScope, pctLine, pctOfMetric, scopedMetricValue, scopedMetricValueForMonths } from './gaReporting';
import { useEffect, useMemo, useRef, useState } from 'react';
import './MarketingSummaryDashboard.css';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

function fmtK(value) {
    const num = Number(value || 0);
    const sign = num < 0 ? '-' : '';
    return `${sign}$${(Math.abs(num) / 1000).toFixed(0)}k`;
}

// $1,000+ collapses to a unit suffix (K/M/B) so KPI tiles and table cells
// stay readable at a glance — one decimal place, trimmed when it's a whole
// number (e.g. "$1.2M" but "$5M", not "$5.0M").
// Every figure is expressed in thousands with a trailing "K" — no M/B
// switch at larger magnitudes — so every card/table on this dashboard uses
// one consistent unit (matches gaReporting.js's money()).
function fmtMoney(value) {
    if (value == null) return 'Unavailable';
    const num = Number(value || 0);
    const sign = num < 0 ? '-' : '';
    const thousands = Math.round(Math.abs(num) / 1000);
    return `${sign}$${thousands.toLocaleString('en-US')}K`;
}

function fmtMonthLabel(iso) {
    if (!iso) return '—';
    const [year, month] = iso.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function useMeasuredWidth(fallback = 700) {
    const [width, setWidth] = useState(fallback);
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const update = () => setWidth(w => (el.clientWidth || w));
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return [ref, width];
}

// ── Chart 1: grouped bars (one bar per series per period) — YTD comparison ──

function GroupedBarChart({ groups, series, height = 220, onGroupClick }) {
    const [wrapRef, measuredWidth] = useMeasuredWidth(700);
    if (!groups.length) return <div className="esd-chart-empty">No data available.</div>;

    const width = Math.max(320, measuredWidth);
    const pad = { left: 44, right: 10, top: 24, bottom: 24 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const max = Math.max(...groups.flatMap(g => series.map(s => s.values[g.key] || 0)), 1);
    const groupStep = innerW / groups.length;
    const barGap = 3;
    const barW = Math.max(4, (groupStep - barGap * (series.length + 1)) / series.length);

    return (
        <div ref={wrapRef}>
            <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height}>
                {[0, .25, .5, .75, 1].map(ratio => {
                    const y = pad.top + innerH * (1 - ratio);
                    return (
                        <g key={ratio}>
                            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#eef1f5" />
                            <text x={pad.left - 6} y={y + 3} textAnchor="end" fontSize="8" fill="#94a3b8">{fmtK(max * ratio)}</text>
                        </g>
                    );
                })}
                {groups.map((group, gi) => {
                    const groupX = pad.left + gi * groupStep;
                    return (
                        <g
                            key={group.key}
                            onClick={() => onGroupClick?.(group.key)}
                            style={{ cursor: onGroupClick ? 'pointer' : 'default' }}
                        >
                            {onGroupClick && (
                                <rect className="esd-chart-hit" x={groupX} y={pad.top} width={groupStep} height={innerH} fill="transparent" rx="4" />
                            )}
                            {series.map((s, si) => {
                                const value = s.values[group.key] || 0;
                                const barH = Math.max(1, (value / max) * innerH);
                                const x = groupX + barGap + si * (barW + barGap);
                                const y = pad.top + innerH - barH;
                                return (
                                    <g key={s.name}>
                                        <rect x={x} y={y} width={barW} height={barH} fill={s.color} rx="2" />
                                        <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#334155">{fmtK(value)}</text>
                                    </g>
                                );
                            })}
                            <text x={groupX + groupStep / 2} y={height - 6} textAnchor="middle" fontSize="9" fill="#6b7280">{group.label}</text>
                        </g>
                    );
                })}
            </svg>
            <div className="esd-chart-legend">
                {series.map(s => (
                    <span key={s.name} className="esd-chart-legend-item">
                        <span className="esd-chart-legend-swatch" style={{ background: s.color }} />
                        {s.name}
                    </span>
                ))}
            </div>
        </div>
    );
}

// Pushes `naturalY` away from every y in `occupied` until at least `minGap`
// apart from each, moving toward whichever side is less crowded — used to
// keep a line-series label from landing on top of a bar segment's own label
// (or another line's), which otherwise happens whenever their values are
// close since both anchor to roughly the same x/y.
function declutterY(naturalY, occupied, minGap, bounds) {
    let y = naturalY;
    for (let guard = 0; guard < 8; guard += 1) {
        let conflict = null;
        for (const oy of occupied) {
            if (Math.abs(oy - y) < minGap && (conflict === null || Math.abs(oy - y) < Math.abs(conflict - y))) {
                conflict = oy;
            }
        }
        if (conflict === null) break;
        y = conflict > y ? conflict - minGap : conflict + minGap;
    }
    if (bounds) y = Math.min(bounds.max, Math.max(bounds.min, y));
    return y;
}

// ── Chart 2/3/4: stacked bars (e.g. HC + Non-HC) with optional line overlays
// (e.g. AOP, PY) sharing the same x-axis — reused for the monthly combo
// chart, the YTD HC-vs-Non-HC breakdown, and the 3-scenario comparison. ──

function StackedComboChart({ groups, stackSeries, lineSeries = [], height = 230, onGroupClick }) {
    const [wrapRef, measuredWidth] = useMeasuredWidth(700);
    if (!groups.length) return <div className="esd-chart-empty">No data available.</div>;

    const width = Math.max(320, measuredWidth);
    const pad = { left: 44, right: 10, top: 24, bottom: 24 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;

    const stackTotal = (key) => stackSeries.reduce((sum, s) => sum + (s.values[key] || 0), 0);
    const max = Math.max(
        ...groups.map(g => stackTotal(g.key)),
        ...lineSeries.flatMap(s => groups.map(g => s.values[g.key] || 0)),
        1,
    );
    const groupStep = innerW / groups.length;
    const barW = Math.min(46, groupStep * 0.5);

    const pointX = (gi) => pad.left + gi * groupStep + groupStep / 2;
    const pointY = (value) => pad.top + innerH - (value / max) * innerH;

    // One pass, up front: for every group, find the y-center of each visible
    // bar-segment label (the fixed obstacles), then resolve each line
    // series' label position against those plus every line label already
    // placed in that same column — so a whole column's labels clear each
    // other in one go instead of each line only knowing about itself.
    const lineLabelY = groups.map((group, gi) => {
        let cumulative = 0;
        const occupied = [];
        stackSeries.forEach(s => {
            const value = s.values[group.key] || 0;
            const barH = Math.max(0, (value / max) * innerH);
            const segY = pad.top + innerH - cumulative - barH;
            cumulative += barH;
            if (barH > 14) occupied.push(segY + barH / 2);
        });
        const bounds = { min: pad.top + 5, max: pad.top + innerH - 2 };
        return lineSeries.map(s => {
            const natural = pointY(s.values[group.key] || 0) - 9;
            const resolved = declutterY(natural, occupied, 11, bounds);
            occupied.push(resolved);
            return resolved;
        });
    });

    return (
        <div ref={wrapRef}>
            <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height}>
                {[0, .25, .5, .75, 1].map(ratio => {
                    const y = pad.top + innerH * (1 - ratio);
                    return (
                        <g key={ratio}>
                            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#eef1f5" />
                            <text x={pad.left - 6} y={y + 3} textAnchor="end" fontSize="8" fill="#94a3b8">{fmtK(max * ratio)}</text>
                        </g>
                    );
                })}
                {groups.map((group, gi) => {
                    const x = pointX(gi) - barW / 2;
                    let cumulative = 0;
                    return (
                        <g
                            key={group.key}
                            onClick={() => onGroupClick?.(group.key)}
                            style={{ cursor: onGroupClick ? 'pointer' : 'default' }}
                        >
                            {onGroupClick && (
                                <rect className="esd-chart-hit" x={pad.left + gi * groupStep} y={pad.top} width={groupStep} height={innerH} fill="transparent" rx="4" />
                            )}
                            {stackSeries.map(s => {
                                const value = s.values[group.key] || 0;
                                const barH = Math.max(0, (value / max) * innerH);
                                const y = pad.top + innerH - cumulative - barH;
                                cumulative += barH;
                                return (
                                    <g key={s.name}>
                                        <rect x={x} y={y} width={barW} height={barH} fill={s.color} />
                                        {barH > 14 && (
                                            <text x={x + barW / 2} y={y + barH / 2 + 3} textAnchor="middle" fontSize="7" fontWeight="700" fill="#fff">{fmtK(value)}</text>
                                        )}
                                    </g>
                                );
                            })}
                            <text x={pointX(gi)} y={height - 6} textAnchor="middle" fontSize="9" fill="#6b7280">{group.label}</text>
                        </g>
                    );
                })}
                {lineSeries.map((s, si) => {
                    const points = groups.map((g, gi) => `${pointX(gi)},${pointY(s.values[g.key] || 0)}`).join(' ');
                    return (
                        <g key={s.name}>
                            <polyline points={points} fill="none" stroke={s.color} strokeWidth="2" strokeDasharray={s.dashed ? '4 3' : undefined} />
                            {groups.map((g, gi) => {
                                const value = s.values[g.key] || 0;
                                const label = fmtK(value);
                                const labelW = label.length * 4.6 + 6;
                                const lx = pointX(gi);
                                const py = pointY(value);
                                // ly is pre-resolved (declutterY, above) to clear both this
                                // column's bar-segment labels and any other line's label —
                                // a thin leader line ties the label back to its actual point
                                // whenever it had to move away to make room.
                                const ly = lineLabelY[gi][si];
                                const moved = Math.abs(ly - (py - 9)) > 1;
                                return (
                                    <g key={g.key}>
                                        <circle cx={lx} cy={py} r="2.6" fill={s.color} />
                                        {moved && <line x1={lx} y1={py} x2={lx} y2={ly + 4} stroke={s.color} strokeWidth="1" strokeOpacity="0.5" />}
                                        <rect x={lx - labelW / 2} y={ly - 7.5} width={labelW} height={10} rx="3" fill="#fff" fillOpacity="0.92" />
                                        <text x={lx} y={ly} textAnchor="middle" fontSize="7" fontWeight="700" fill={s.color}>{label}</text>
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
            <div className="esd-chart-legend">
                {[...stackSeries, ...lineSeries].map(s => (
                    <span key={s.name} className="esd-chart-legend-item">
                        <span className={`esd-chart-legend-swatch${s.dashed !== undefined ? ' is-line' : ''}`} style={{ background: s.color }} />
                        {s.name}
                    </span>
                ))}
            </div>
        </div>
    );
}

// ── KPI card — one shared building block for every hero-metric tile ───────

function KpiCard({ tone, icon, label, value, sub, valueTone, subTone, hint = 'View detail', onClick }) {
    const Tag = onClick ? 'button' : 'div';
    return (
        <Tag type={onClick ? 'button' : undefined} className={`esd-kpi-card esd-kpi-card--${tone}`} onClick={onClick}>
            <div className="esd-kpi-top">
                <span className="esd-kpi-icon">{icon}</span>
                {onClick && hint && <span className="esd-kpi-hint">{hint} →</span>}
            </div>
            <div className="esd-kpi-label">{label}</div>
            <div className={`esd-kpi-value${valueTone ? ` tone-${valueTone}` : ''}`}>{value}</div>
            {sub && <div className={`esd-kpi-sub${subTone ? ` tone-${subTone}` : ''}`}>{sub}</div>}
        </Tag>
    );
}

// Percentage variance = (Actual − Comparison) / |Comparison| × 100 — the
// caller passes that raw ratio (already ×100) as `pct`; this is the one
// place it gets formatted, always to exactly 2 decimal places, so no caller
// can regress back to a raw float with a dozen digits. `pct` is null when
// there's no comparison figure to divide by (e.g. no prior year selected),
// in which case the percentage line is omitted rather than showing "null%".
function VarianceKpi({ label, amount, pct, compareLabel = 'vs. AOP', onClick }) {
    const favorable = amount <= 0;
    return (
        <KpiCard
            tone={favorable ? 'good' : 'bad'}
            icon={favorable ? '✅' : '⚠️'}
            label={label}
            value={fmtMoney(amount)}
            valueTone={favorable ? 'good' : 'bad'}
            sub={Number.isFinite(pct) ? `${pct.toFixed(2)}% ${compareLabel}` : undefined}
            subTone={favorable ? 'good' : 'bad'}
            onClick={onClick}
        />
    );
}

// ── Deep-dive modal — opened from any KPI card, chart bar, variance-cascade
// quarter, or department row. Shows the complete HC/Non-HC/Total monthly
// detail behind whatever was clicked, plus the category breakdown for that
// same scope (or, when a specific category was clicked, just that
// category's monthly detail) — all computed client-side from the data
// already fetched for the page, so no extra API round-trip is needed. ──────

// Category (HC/Non-HC) and Fiscal Year (Current/Prior) are both multi-select
// now — Category's membership test replaces the old single-value equality
// check (empty selection = both included, same meaning as old 'total'), and
// Fiscal Year blends by computing each selected mode's own figures and
// summing them (there's no single equality check that means "both years"),
// same reasoning as gaReporting.js's scopeSummaryRow/scopedMetricValue.
const MONTH_DETAIL_KEYS = ['hc_actual', 'nonhc_actual', 'hc_aop', 'nonhc_aop', 'hc_py', 'nonhc_py'];
const sumByKeys = (objs, keys) => Object.fromEntries(keys.map(k => [k, objs.reduce((s, o) => s + (o[k] || 0), 0)]));
const normalizeYearModesLocal = (yearMode) => {
    const modes = Array.isArray(yearMode) ? yearMode : (yearMode ? [yearMode] : []);
    return modes.length ? modes : ['current'];
};

function monthlyDetailForScope(data, gaSummary, months, category, sourceFilter = [], yearMode = 'current') {
    const yearModes = normalizeYearModesLocal(yearMode);
    const hcIncluded = !sourceFilter.length || sourceFilter.includes('hc');
    const nonhcIncluded = !sourceFilter.length || sourceFilter.includes('nonhc');
    // Department Sub-Category is multi-select too — when more than one is
    // selected, sum their looked-up monthly rows together instead of using
    // an array as a single object key (which would silently match nothing).
    const categories = Array.isArray(category) ? category : (category ? [category] : []);
    if (categories.length) {
        const hcMonthlies = categories.map(cat => (hcIncluded ? (gaSummary?.headcount_costs?.by_category?.[cat]?.monthly || []) : []));
        const nonhcMonthlies = categories.map(cat => (nonhcIncluded ? (gaSummary?.non_headcount_costs?.by_category?.[cat]?.monthly || []) : []));
        return months.map(month => {
            const hcRows = hcMonthlies.map(rows => rows.find(m => m.month === month) || {});
            const nonhcRows = nonhcMonthlies.map(rows => rows.find(m => m.month === month) || {});
            const label = hcRows.find(r => r.label)?.label || nonhcRows.find(r => r.label)?.label || month;
            const sumField = (rows, field) => rows.reduce((s, r) => s + (r[field] || 0), 0);
            const perMode = yearModes.map(ym => (ym === 'prior'
                ? { hc_actual: sumField(hcRows, 'py'), nonhc_actual: sumField(nonhcRows, 'py'), hc_aop: 0, nonhc_aop: 0, hc_py: 0, nonhc_py: 0 }
                : { hc_actual: sumField(hcRows, 'actual'), nonhc_actual: sumField(nonhcRows, 'actual'), hc_aop: sumField(hcRows, 'aop'), nonhc_aop: sumField(nonhcRows, 'aop'), hc_py: sumField(hcRows, 'py'), nonhc_py: sumField(nonhcRows, 'py') }));
            return { month, label, ...sumByKeys(perMode, MONTH_DETAIL_KEYS) };
        });
    }
    const rows = months
        .map(month => data.monthly_table.find(r => r.month === month))
        .filter(Boolean);
    return rows
        .map(r => ({
            ...r,
            hc_actual: hcIncluded ? r.hc_actual : 0, hc_aop: hcIncluded ? r.hc_aop : 0, hc_py: hcIncluded ? r.hc_py : 0,
            nonhc_actual: nonhcIncluded ? r.nonhc_actual : 0, nonhc_aop: nonhcIncluded ? r.nonhc_aop : 0, nonhc_py: nonhcIncluded ? r.nonhc_py : 0,
        }))
        .map(r => {
            const perMode = yearModes.map(ym => (ym === 'prior'
                ? { hc_actual: r.hc_py, nonhc_actual: r.nonhc_py, hc_aop: 0, nonhc_aop: 0, hc_py: 0, nonhc_py: 0 }
                : { hc_actual: r.hc_actual, nonhc_actual: r.nonhc_actual, hc_aop: r.hc_aop, nonhc_aop: r.nonhc_aop, hc_py: r.hc_py, nonhc_py: r.nonhc_py }));
            return { ...r, ...sumByKeys(perMode, MONTH_DETAIL_KEYS) };
        });
}

// ── Department + quarter filters: rebuilds the whole "data" shape (KPIs,
// monthly table, run rate, variance) scoped to one department and/or one
// fiscal quarter, reusing monthlyDetailForScope's per-category monthly
// series. No extra API call — gaSummary already has every category's full
// monthly detail, and the unfiltered "data" already has all 12 months. ────

function buildScopedData(data, gaSummary, department, quarter, customRange, sourceFilter = [], yearMode = 'current', period = []) {


    const cutoffMonth = data.monthly_table[data.variance_to_aop_monthly.length - 1]?.month;

    // A month counts as in scope if it's in scope for ANY selected Fiscal
    // Year mode (matters only when a custom date range is active, since
    // that's the one month-in-scope check yearMode affects).
    const allMonths = data.monthly_table
        .map(r => r.month)
        .filter(m => normalizeYearModesLocal(yearMode).some(ym => monthInScope(m, { quarter, customRange, period, asOf: data.as_of, yearMode: ym, priorYear: String(Number(m.slice(0, 4)) - 1) })));
    // Category (HC/Non-HC) and Year (current/prior) filters are applied
    // inside monthlyDetailForScope, shared with the deep-dive modals below,
    // so every level of the page reshapes the same way.
    const scopedRows = monthlyDetailForScope(data, gaSummary, allMonths, department, sourceFilter, yearMode);
    const monthly_table = scopedRows.map(r => ({
        ...r,
        total_actual: (r.hc_actual || 0) + (r.nonhc_actual || 0),
        total_aop: (r.hc_aop || 0) + (r.nonhc_aop || 0),
        total_py: (r.hc_py || 0) + (r.nonhc_py || 0),
    }));

    // "YTD" here means "months in scope that are on or before the
    // workbook's own Actual/FCST cutoff" — computed by date rather than by
    // slicing a fixed count, since a quarter filter can leave fewer than 12
    // months in monthly_table.
    const ytdRows = cutoffMonth ? monthly_table.filter(r => r.month <= cutoffMonth) : monthly_table;
    const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);

    const ytd_hc_actual = sum(ytdRows, 'hc_actual'), ytd_nonhc_actual = sum(ytdRows, 'nonhc_actual');
    const ytd_hc_aop = sum(ytdRows, 'hc_aop'), ytd_nonhc_aop = sum(ytdRows, 'nonhc_aop');
    const ytd_hc_py = sum(ytdRows, 'hc_py'), ytd_nonhc_py = sum(ytdRows, 'nonhc_py');
    const ytd_actual = ytd_hc_actual + ytd_nonhc_actual;
    const ytd_aop = ytd_hc_aop + ytd_nonhc_aop;
    const ytd_py = ytd_hc_py + ytd_nonhc_py;

    const fy_actual = sum(monthly_table, 'total_actual');
    const fy_aop = sum(monthly_table, 'total_aop');
    const fy_py = sum(monthly_table, 'total_py');

    const variance = (actual, plan) => ({
        amount: actual - plan,
        pct: plan ? Math.round((actual - plan) / plan * 10000) / 100 : 0,
    });

    const split = runRateSplit(gaSummary, department, sourceFilter, yearMode);
    const run_rate = sumRunRates([split.hc, split.nonhc]);

    const variance_to_aop_monthly = ytdRows.map(r => {
        const hc = (r.hc_actual || 0) - (r.hc_aop || 0);
        const nonhc = (r.nonhc_actual || 0) - (r.nonhc_aop || 0);
        return { month: r.month, label: r.label, hc, nonhc, total: hc + nonhc };
    });
    const variance_to_aop_ytd_total = {
        hc: sum(variance_to_aop_monthly, 'hc'),
        nonhc: sum(variance_to_aop_monthly, 'nonhc'),
        total: sum(variance_to_aop_monthly, 'total'),
    };

    return {
        ...data,
        monthly_table,
        ytd: {
            actual: ytd_actual, aop: ytd_aop, prior_year: ytd_py,
            vs_aop: variance(ytd_actual, ytd_aop),
            hc_actual: ytd_hc_actual, nonhc_actual: ytd_nonhc_actual,
            hc_aop: ytd_hc_aop, nonhc_aop: ytd_nonhc_aop,
            hc_py: ytd_hc_py, nonhc_py: ytd_nonhc_py,
        },
        full_year: {
            actual_forecast: fy_actual, aop: fy_aop, prior_year: fy_py,
            vs_aop: variance(fy_actual, fy_aop),
            yoy_growth_pct: fy_py ? Math.round((fy_actual - fy_py) / fy_py * 10000) / 100 : 0,
        },
        run_rate,
        variance_to_aop_monthly,
        variance_to_aop_ytd_total,
    };
}

function categoryBreakdownForScope(gaSummary, months, sourceFilter = [], yearMode = 'current') {
    if (!gaSummary) return [];
    const yearModes = normalizeYearModesLocal(yearMode);
    const hcIncluded = !sourceFilter.length || sourceFilter.includes('hc');
    const nonhcIncluded = !sourceFilter.length || sourceFilter.includes('nonhc');
    return (gaSummary.categories || []).map(cat => {
        const hcMonthly = hcIncluded ? (gaSummary.headcount_costs?.by_category?.[cat]?.monthly || []) : [];
        const nonhcMonthly = nonhcIncluded ? (gaSummary.non_headcount_costs?.by_category?.[cat]?.monthly || []) : [];
        const sumFor = (rows, key) => rows.filter(r => months.includes(r.month)).reduce((s, r) => s + (r[key] || 0), 0);
        const perMode = yearModes.map(ym => ({
            actual: sumFor(hcMonthly, ym === 'prior' ? 'py' : 'actual') + sumFor(nonhcMonthly, ym === 'prior' ? 'py' : 'actual'),
            aop: ym === 'prior' ? 0 : sumFor(hcMonthly, 'aop') + sumFor(nonhcMonthly, 'aop'),
            py: ym === 'prior' ? 0 : sumFor(hcMonthly, 'py') + sumFor(nonhcMonthly, 'py'),
        }));
        const totals = perMode.reduce((a, b) => ({ actual: a.actual + b.actual, aop: a.aop + b.aop, py: a.py + b.py }), { actual: 0, aop: 0, py: 0 });
        return { category: cat, ...totals };
    }).filter(row => row.actual || row.aop || row.py);
}

// Month-by-month % of Revenue/ARR for the PctDeepDiveModal's Monthly Detail
// table — divides each month's $ total by that SAME month's Revenue/ARR
// value (not the scoped/FY total used for the headline % cards), so a
// slow-revenue month still shows its own real ratio instead of borrowing
// the whole period's blended denominator.
function monthlyPctRows(monthlyTable, metricSeries, months) {
    const byMonth = Object.fromEntries((metricSeries?.monthly || []).map(m => [m.month, m]));
    return months
        .map(month => monthlyTable.find(r => r.month === month))
        .filter(Boolean)
        .map(row => {
            const denom = byMonth[row.month] || {};
            const pct = (num, field) => (denom[field] ? (num / denom[field]) * 100 : null);
            const actualPct = pct(row.total_actual, 'actual');
            const aopPct = pct(row.total_aop, 'aop');
            return {
                label: row.label,
                actual: actualPct,
                aop: aopPct,
                py: pct(row.total_py, 'py'),
                // Point-difference between this month's own Actual% and
                // AOP% (not a $ variance ÷ revenue) — same reasoning as the
                // headline vs. AOP Variance card, so this breakdown always
                // reconciles with it.
                variance: actualPct != null && aopPct != null ? actualPct - aopPct : null,
                _raw: {
                    actual: { num: row.total_actual, denom: denom.actual },
                    aop: { num: row.total_aop, denom: denom.aop },
                    py: { num: row.total_py, denom: denom.py },
                },
            };
        });
}

// Each category's own $ total (from categoryBreakdownForScope) as a % of
// the SAME scoped Revenue/ARR denominator the headline % cards use — same
// company-wide-denominator convention as GASummary.js's By Category % of
// Revenue/ARR tables (Revenue/ARR isn't tracked per category, so every
// category's % is its own share of the one company figure).
function categoryPctRows(gaSummary, months, sourceFilter, yearMode, denomActual, denomAop, denomPy) {
    return categoryBreakdownForScope(gaSummary, months, sourceFilter, yearMode).map(row => {
        const actualPct = denomActual ? (row.actual / denomActual) * 100 : null;
        const aopPct = denomAop ? (row.aop / denomAop) * 100 : null;
        return {
            label: row.category,
            actual: actualPct,
            aop: aopPct,
            py: denomPy ? (row.py / denomPy) * 100 : null,
            // Point-difference between this category's own Actual% and
            // AOP% — see monthlyPctRows' identical comment.
            variance: actualPct != null && aopPct != null ? actualPct - aopPct : null,
            _raw: {
                actual: { num: row.actual, denom: denomActual },
                aop: { num: row.aop, denom: denomAop },
                py: { num: row.py, denom: denomPy },
            },
        };
    });
}

// Same company-wide-denominator convention, but for each category's own
// annualized Run Rate (current/dec_aop/dec_fcst) instead of a $ total.
// Mapped onto the modal's generic {actual, aop, py, variance} column slots
// (Current -> actual, Dec AOP -> aop, Dec Fcst -> py) so PctDeepDiveModal's
// Category Breakdown table doesn't need two different row shapes — only
// the column headers passed alongside it change.
function categoryRunRatePctRows(gaSummary, sourceFilter, yearMode, denomCurrent, denomDecAop, denomDecFcst) {
    return (gaSummary?.categories || [])
        .map(cat => {
            const split = runRateSplit(gaSummary, cat, sourceFilter, yearMode);
            const combined = sumRunRates([split.hc, split.nonhc]);
            return { label: cat, ...combined };
        })
        .filter(r => r.current || r.dec_aop || r.dec_fcst)
        .map(r => {
            const actual = denomCurrent && r.current != null ? (r.current / denomCurrent) * 100 : null;
            const aop = denomDecAop && r.dec_aop != null ? (r.dec_aop / denomDecAop) * 100 : null;
            const py = denomDecFcst && r.dec_fcst != null ? (r.dec_fcst / denomDecFcst) * 100 : null;
            const variance = denomCurrent && r.dec_fcst != null && r.dec_aop != null ? ((r.dec_fcst - r.dec_aop) / denomCurrent) * 100 : null;
            return {
                label: r.label, actual, aop, py, variance,
                _raw: {
                    actual: { num: r.current, denom: denomCurrent },
                    aop: { num: r.dec_aop, denom: denomDecAop },
                    py: { num: r.dec_fcst, denom: denomDecFcst },
                    variance: { num: r.dec_fcst != null && r.dec_aop != null ? r.dec_fcst - r.dec_aop : null, denom: denomCurrent },
                },
            };
        });
}


// All run-rate totals come from the shared backend calculation.
function categoryRunRateSplit(gaSummary, category, sourceFilter = [], yearMode = 'current') {
    return runRateSplit(gaSummary, category, sourceFilter, yearMode);
}

function fmtPct(value) {
    if (!Number.isFinite(value)) return null;
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
}

// A variance amount plus its percentage against `base`, stacked in one
// cell — the standard FP&A "$ over/under, and by how much %" pairing, used
// everywhere a Variance to AOP / Variance to Prior Year column shows up.
// Same "lower than plan is good" convention as VariancePill/VarianceCell.
function VarianceStack({ actual, base }) {
    if (actual == null || base == null) return <span>Unavailable</span>;
    const diff = actual - base;
    const pct = base ? fmtPct((diff / Math.abs(base)) * 100) : null;
    const favorable = diff <= 0;
    const color = favorable ? '#15803d' : '#dc2626';
    return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
            <span style={{ color, fontWeight: 700 }}>{fmtMoney(diff)}</span>
            {pct && <span style={{ color, opacity: .72, fontSize: '0.85em', fontWeight: 600 }}>{pct}</span>}
        </span>
    );
}

// ── Shared drill-modal shell — one consistent header (icon, title, sub, a
// clickable breadcrumb trail, close) and body wrapper for every layer of the
// deep-dive (DeepDiveModal, RecordsListModal). The breadcrumb replaces the
// old single "← Back" button: every earlier layer is a clickable crumb, so
// jumping from "Employees — Jan" straight back to "YTD Actuals" is one
// click instead of three. ───────────────────────────────────────────────────
function DrillModalShell({ icon, title, sub, breadcrumb, onJump, onClose, onBack, maxWidth = 720, children }) {
    // onBack, when explicitly passed, overrides the usual "pop one breadcrumb
    // level" default — RecordsListModal uses this to pop its own inner
    // vendor-list -> category-list toggle first, one hop at a time, before
    // falling through to the outer breadcrumb once that inner state is clear.
    const effectiveOnBack = onBack !== undefined ? onBack : (breadcrumb?.length > 1 ? () => onJump(breadcrumb.length - 2) : undefined);
    return (
        <div className="legal-modal-bg esd-drill-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal esd esd-drill-modal esd-compact-modal" style={{ maxWidth }}>
                <div className="legal-modal-hdr esd-drill-hdr">
                    <div className="legal-modal-icon esd-drill-icon"><DetailIcon name="person" /></div>
                    <div className="esd-drill-hdr-text">
                        <div className="legal-modal-title">{title}</div>
                        <div className="legal-modal-sub">{sub}</div>
                    </div>
                    <DetailModalControls onBack={effectiveOnBack} onClose={onClose} />
                </div>
                {breadcrumb && breadcrumb.length > 1 && (
                    <div className="esd-breadcrumb">
                        {breadcrumb.map((crumb, i) => (
                            <span className="esd-breadcrumb-item" key={i}>
                                {i > 0 && <span className="esd-breadcrumb-sep">›</span>}
                                {i === breadcrumb.length - 1
                                    ? <span className="esd-breadcrumb-current">{crumb}</span>
                                    : <button type="button" className="esd-breadcrumb-link" onClick={() => onJump(i)}>{crumb}</button>}
                            </span>
                        ))}
                    </div>
                )}
                <div className="legal-modal-body esd-drill-body">{children}</div>
            </div>
        </div>
    );
}

// Percentage cards (% of Revenue / % of ARR) open this instead of the
// dollar-figure DeepDiveModal/RunRateSplitModal their paired $ card opens —
// the same KpiCard tiles the page itself uses (not a $ monthly table), just
// the full Actuals/AOP/PY/Variance (or Run Rate) % breakdown for whichever
// metric (Revenue or ARR) was clicked, so every related % lands in one
// place instead of one modal per card.
function pctCell(v) {
    return v != null ? `${v.toFixed(1)}%` : '—';
}

// Percentage cards (% of Revenue / % of ARR) open this instead of the
// dollar-figure DeepDiveModal/RunRateSplitModal their paired $ card opens —
// the same KpiCard tiles the page itself uses (not a $ monthly table) for
// the headline Actuals/AOP/PY/Variance (or Run Rate) % breakdown, plus a
// Monthly Detail table (Snapshot cards only — Run Rate is always
// annualized, no month-by-month trend to show) and a Category Breakdown
// table, both expressed the same way: every figure is a percentage, never
// a dollar amount, so this modal never shows the same numbers its paired
// $ card's own DeepDiveModal already does.
function PctFormulaModal({ label, value, formula, numerator, denominator, minuendPct, subtrahendPct, onClose }) {
    // vs. AOP Variance cards pass the two percentages plugged into the
    // subtraction (minuendPct − subtrahendPct) instead, since that formula
    // has no single numerator/denominator to show.
    const hasValues = numerator != null && denominator;
    const hasDifference = minuendPct != null && subtrahendPct != null;
    return (
        <DrillModalShell icon="🧮" title={label} sub="Formula" onClose={onClose} maxWidth={440}>
            <div className="esd-kpi-grid" style={{ marginBottom: 0 }}>
                <div className="esd-kpi-card esd-kpi-card--b">
                    <div className="esd-kpi-label">{label}</div>
                    <div className="esd-kpi-value">{value}</div>
                    <div className="esd-kpi-sub">Formula: {formula}</div>
                    {hasValues && <div className="esd-kpi-sub">= {fmtMoney(numerator)} ÷ {fmtMoney(denominator)} × 100 = {value}</div>}
                    {hasDifference && <div className="esd-kpi-sub">= {minuendPct.toFixed(1)}% − {subtrahendPct.toFixed(1)}% = {value}</div>}
                </div>
            </div>
        </DrillModalShell>
    );
}

function PctDeepDiveModal({ title, sub, cards, monthlyRows, categoryRows, categoryHeaders = ['Actuals', 'AOP', 'Prior Year', 'vs. AOP Variance'], metricLabel = 'Revenue', onClose }) {
    const [formulaDrill, setFormulaDrill] = useState(null);
    const monthlyFormulas = {
        actual: `That month's Actuals+Forecast ÷ that month's ${metricLabel} × 100`,
        aop: `That month's AOP Budget ÷ that month's AOP ${metricLabel} × 100`,
        py: `That month's Prior Year Actuals ÷ that month's Prior Year ${metricLabel} × 100`,
        variance: `That month's Actuals+Forecast % of ${metricLabel} − that month's AOP % of ${metricLabel}`,
    };
    const categoryFormulas = {
        actual: `This category's Actuals+Forecast ÷ ${metricLabel} for the selected period × 100`,
        aop: `This category's AOP Budget ÷ AOP ${metricLabel} for the selected period × 100`,
        py: `This category's Prior Year Actuals ÷ Prior Year ${metricLabel} for the selected period × 100`,
        variance: `This category's Actuals+Forecast % of ${metricLabel} − this category's AOP % of ${metricLabel}`,
    };
    const columnLabel = { actual: categoryHeaders[0], aop: categoryHeaders[1], py: categoryHeaders[2], variance: categoryHeaders[3] };
    const openCell = (row, field, formulas) => setFormulaDrill({
        label: `${row.label} — ${columnLabel[field]}`,
        value: pctCell(row[field]),
        formula: formulas[field],
        numerator: row._raw?.[field]?.num,
        denominator: row._raw?.[field]?.denom,
        ...(field === 'variance' ? { minuendPct: row.actual, subtrahendPct: row.aop } : {}),
    });

    return (
        <DrillModalShell icon="📊" title={title} sub={sub} onClose={onClose} maxWidth={960}>
            <div className="esd-kpi-grid" style={{ marginBottom: monthlyRows || categoryRows?.length ? 20 : 0 }}>
                {cards.map(c => (
                    <KpiCard key={c.label} {...c} hint="View formula" onClick={() => setFormulaDrill({ label: c.label, value: c.value, formula: c.formula, numerator: c.numerator, denominator: c.denominator, minuendPct: c.minuendPct, subtrahendPct: c.subtrahendPct })} />
                ))}
            </div>

            {monthlyRows && (
                <div className="esd-section">
                    <div className="esd-section-head">
                        <span className="esd-section-icon">🗓️</span>
                        <span className="esd-section-title">Monthly Detail</span>
                        <span className="esd-section-note">Click a figure for its formula</span>
                    </div>
                    <div className="esd-table-wrap">
                        <table className="esd-table esd-table-cellclick">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Month</th>
                                    <th>Actuals</th>
                                    <th>AOP</th>
                                    <th>Prior Year</th>
                                    <th>vs. AOP Variance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyRows.map(row => (
                                    <tr key={row.label}>
                                        <td style={{ textAlign: 'left' }}>{row.label}</td>
                                        <td onClick={() => openCell(row, 'actual', monthlyFormulas)}><strong>{pctCell(row.actual)}</strong></td>
                                        <td onClick={() => openCell(row, 'aop', monthlyFormulas)}><strong>{pctCell(row.aop)}</strong></td>
                                        <td onClick={() => openCell(row, 'py', monthlyFormulas)}><strong>{pctCell(row.py)}</strong></td>
                                        <td onClick={() => openCell(row, 'variance', monthlyFormulas)}><strong>{pctCell(row.variance)}</strong></td>
                                    </tr>
                                ))}
                                {!monthlyRows.length && (
                                    <tr><td colSpan={5} className="esd-empty-row">No monthly data in this scope.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!!categoryRows?.length && (
                <div className="esd-section">
                    <div className="esd-section-head">
                        <span className="esd-section-icon">🏷️</span>
                        <span className="esd-section-title">Category Breakdown</span>
                        <span className="esd-section-note">Click a figure for its formula</span>
                    </div>
                    <div className="esd-table-wrap">
                        <table className="esd-table esd-table-cellclick">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Category</th>
                                    <th>{categoryHeaders[0]}</th>
                                    <th>{categoryHeaders[1]}</th>
                                    <th>{categoryHeaders[2]}</th>
                                    <th>{categoryHeaders[3]}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categoryRows.map(row => (
                                    <tr key={row.label}>
                                        <td style={{ textAlign: 'left' }}>{row.label}</td>
                                        <td onClick={() => openCell(row, 'actual', categoryFormulas)}><strong>{pctCell(row.actual)}</strong></td>
                                        <td onClick={() => openCell(row, 'aop', categoryFormulas)}><strong>{pctCell(row.aop)}</strong></td>
                                        <td onClick={() => openCell(row, 'py', categoryFormulas)}><strong>{pctCell(row.py)}</strong></td>
                                        <td onClick={() => openCell(row, 'variance', categoryFormulas)}><strong>{pctCell(row.variance)}</strong></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {formulaDrill && <PctFormulaModal {...formulaDrill} onClose={() => setFormulaDrill(null)} />}
        </DrillModalShell>
    );
}

function DeepDiveModal({ title, sub, months, category, department, sourceFilter = [], yearMode = 'current', data, gaSummary, breadcrumb, onJump, onClose, onDrillMonth, onDrillCategory, onDrillTotal, onDrillVariance, onDrillRunRate }) {
    // If a specific category was clicked (e.g. a Department Highlights row),
    // that's the scope. Otherwise, when the page-level department filter is
    // active, the "unscoped" click (a KPI card, a chart bar) is still
    // implicitly scoped to that department — the breakdown table below
    // would otherwise show every other department's numbers, which looks
    // like the filter was ignored. department is an array (multi-select) and
    // always truthy, even empty, so the old `category || department || null`
    // is replaced with an explicit length check.
    const effectiveCategory = category ? [category] : (department && department.length ? department : null);
    const monthlyRows = monthlyDetailForScope(data, gaSummary, months, effectiveCategory, sourceFilter, yearMode);
    const categoryRows = effectiveCategory ? [] : categoryBreakdownForScope(gaSummary, months, sourceFilter, yearMode);

    const totals = monthlyRows.reduce((acc, r) => ({
        hc_actual: acc.hc_actual + (r.hc_actual || 0), nonhc_actual: acc.nonhc_actual + (r.nonhc_actual || 0),
        hc_aop: acc.hc_aop + (r.hc_aop || 0), nonhc_aop: acc.nonhc_aop + (r.nonhc_aop || 0),
        hc_py: acc.hc_py + (r.hc_py || 0), nonhc_py: acc.nonhc_py + (r.nonhc_py || 0),
    }), { hc_actual: 0, nonhc_actual: 0, hc_aop: 0, nonhc_aop: 0, hc_py: 0, nonhc_py: 0 });
    const totalActual = totals.hc_actual + totals.nonhc_actual;
    const totalAop = totals.hc_aop + totals.nonhc_aop;
    const totalPy = totals.hc_py + totals.nonhc_py;
    const totalFavorable = totalActual - totalAop <= 0;
    const totalPyFavorable = totalActual - totalPy <= 0;
    // Synthetic "whole scope" row, same shape as a Monthly Detail row, so the
    // Category Breakdown Total footer and the KPI cards can reuse
    // onDrillTotal/onDrillVariance as-is instead of a bespoke callback for
    // the aggregate case.
    const scopeTotalRow = { month: 'scope-total', _months: months, label: title, ...totals };
    const pctVsAop = totalAop ? fmtPct(((totalActual - totalAop) / Math.abs(totalAop)) * 100) : null;
    const pctVsPy = totalPy ? fmtPct(((totalActual - totalPy) / Math.abs(totalPy)) * 100) : null;
    const kpiHint = onDrillTotal ? 'HC / Non-HC split' : '';

    // % of Revenue / % of ARR for exactly the months this modal is scoped
    // to (a single month, a quarter, whatever was clicked into) — same
    // balance-vs-flow rule as the page-level cards (scopedMetricValueForMonths).
    const revenueArr = data.revenue_arr || {};
    const pctActual = {
        revenue: pctOfMetric(totalActual, scopedMetricValueForMonths(revenueArr.revenue, 'actual', months)),
        arr: pctOfMetric(totalActual, scopedMetricValueForMonths(revenueArr.ending_arr, 'actual', months)),
    };
    const pctAop = {
        revenue: pctOfMetric(totalAop, scopedMetricValueForMonths(revenueArr.revenue, 'aop', months)),
        arr: pctOfMetric(totalAop, scopedMetricValueForMonths(revenueArr.ending_arr, 'aop', months)),
    };

    return (
        <DrillModalShell
            icon="🔎" title={title} sub={sub} maxWidth={920}
            breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}
        >
            <div className="esd-kpi-grid" style={{ marginBottom: 20 }}>
                <KpiCard
                    tone="a" icon="💵" label="Actual/Forecast" value={fmtMoney(totalActual)} hint={kpiHint}
                    sub={pctLine(pctActual)}
                    onClick={() => onDrillTotal?.('actual', scopeTotalRow)}
                />
                <KpiCard
                    tone="b" icon="🎯" label="AOP" value={fmtMoney(totalAop)} hint={kpiHint}
                    sub={pctLine(pctAop)}
                    onClick={() => onDrillTotal?.('aop', scopeTotalRow)}
                />
                <KpiCard
                    tone="c" icon="📅" label="Prior Year" value={fmtMoney(totalPy)} hint={kpiHint}
                    onClick={() => onDrillTotal?.('py', scopeTotalRow)}
                />
                <KpiCard
                    tone={totalFavorable ? 'good' : 'bad'} icon={totalFavorable ? '✅' : '⚠️'}
                    label="Var. to AOP" value={fmtMoney(totalActual - totalAop)}
                    valueTone={totalFavorable ? 'good' : 'bad'}
                    sub={pctVsAop} subTone={totalFavorable ? 'good' : 'bad'}
                    hint={onDrillVariance ? 'HC / Non-HC split' : ''}
                    onClick={() => onDrillVariance?.('var_aop', scopeTotalRow)}
                />
                <KpiCard
                    tone={totalPyFavorable ? 'good' : 'bad'} icon={totalPyFavorable ? '✅' : '⚠️'}
                    label="Var. to PY" value={fmtMoney(totalActual - totalPy)}
                    valueTone={totalPyFavorable ? 'good' : 'bad'}
                    sub={pctVsPy} subTone={totalPyFavorable ? 'good' : 'bad'}
                    hint={onDrillVariance ? 'HC / Non-HC split' : ''}
                    onClick={() => onDrillVariance?.('var_py', scopeTotalRow)}
                />
            </div>

            <div className="esd-section">
                <div className="esd-section-head">
                    <span className="esd-section-icon">🗓️</span>
                    <span className="esd-section-title">Monthly Detail{effectiveCategory ? ` — ${effectiveCategory.join(' + ')}` : ''}</span>
                    <span className="esd-section-note">{onDrillTotal ? 'Click a total for the HC / Non-HC split' : ''}</span>
                </div>
                <div className="esd-table-wrap">
                    <table className="esd-table esd-table-cellclick">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Month</th>
                                <th>Actual/Forecast</th>
                                <th>AOP</th>
                                <th>Prior Year</th>
                                <th>Var. to AOP</th>
                                <th>Var. to PY</th>
                            </tr>
                        </thead>
                        <tbody>
                            {monthlyRows.map(row => {
                                const rowActual = (row.hc_actual || 0) + (row.nonhc_actual || 0);
                                const rowAop = (row.hc_aop || 0) + (row.nonhc_aop || 0);
                                const rowPy = (row.hc_py || 0) + (row.nonhc_py || 0);
                                const totalCell = (kind, value) => (
                                    <td
                                        className={onDrillTotal ? 'esd-cell-click' : undefined}
                                        onClick={() => onDrillTotal?.(kind, row)}
                                        title={onDrillTotal ? `${KIND_LABEL[kind]} — ${row.label} — HC / Non-HC split` : undefined}
                                    >
                                        <strong>{fmtMoney(value)}</strong>
                                    </td>
                                );
                                return (
                                    <tr key={row.month}>
                                        <td
                                            className={onDrillMonth ? 'esd-cell-click' : undefined}
                                            style={{ textAlign: 'left' }}
                                            onClick={() => onDrillMonth?.(row.month, row.label)}
                                            title={onDrillMonth ? `Category breakdown — ${row.label}` : undefined}
                                        >
                                            {row.label}
                                        </td>
                                        {totalCell('actual', rowActual)}
                                        {totalCell('aop', rowAop)}
                                        {totalCell('py', rowPy)}
                                        <td
                                            className={onDrillVariance ? 'esd-cell-click' : undefined}
                                            onClick={() => onDrillVariance?.('var_aop', row)}
                                            title={onDrillVariance ? `Variance to AOP — ${row.label} — HC / Non-HC split` : undefined}
                                        >
                                            <VarianceStack actual={rowActual} base={rowAop} />
                                        </td>
                                        <td
                                            className={onDrillVariance ? 'esd-cell-click' : undefined}
                                            onClick={() => onDrillVariance?.('var_py', row)}
                                            title={onDrillVariance ? `Variance to Prior Year — ${row.label} — HC / Non-HC split` : undefined}
                                        >
                                            <VarianceStack actual={rowActual} base={rowPy} />
                                        </td>
                                    </tr>
                                );
                            })}
                            {!monthlyRows.length && (
                                <tr><td colSpan={6} className="esd-empty-row">No monthly data in this scope.</td></tr>
                            )}
                        </tbody>
                        {!!monthlyRows.length && (
                            <tfoot>
                                <tr>
                                    <td style={{ textAlign: 'left' }}><strong>Period Total</strong></td>
                                    <td
                                        className={onDrillTotal ? 'esd-cell-click' : undefined}
                                        onClick={() => onDrillTotal?.('actual', scopeTotalRow)}
                                        title={onDrillTotal ? 'Actual/Forecast — HC / Non-HC split' : undefined}
                                    ><strong>{fmtMoney(totalActual)}</strong></td>
                                    <td
                                        className={onDrillTotal ? 'esd-cell-click' : undefined}
                                        onClick={() => onDrillTotal?.('aop', scopeTotalRow)}
                                        title={onDrillTotal ? 'AOP — HC / Non-HC split' : undefined}
                                    ><strong>{fmtMoney(totalAop)}</strong></td>
                                    <td
                                        className={onDrillTotal ? 'esd-cell-click' : undefined}
                                        onClick={() => onDrillTotal?.('py', scopeTotalRow)}
                                        title={onDrillTotal ? 'Prior Year — HC / Non-HC split' : undefined}
                                    ><strong>{fmtMoney(totalPy)}</strong></td>
                                    <td
                                        className={onDrillVariance ? 'esd-cell-click' : undefined}
                                        onClick={() => onDrillVariance?.('var_aop', scopeTotalRow)}
                                        title={onDrillVariance ? 'Variance to AOP — HC / Non-HC split' : undefined}
                                    ><VarianceStack actual={totalActual} base={totalAop} /></td>
                                    <td
                                        className={onDrillVariance ? 'esd-cell-click' : undefined}
                                        onClick={() => onDrillVariance?.('var_py', scopeTotalRow)}
                                        title={onDrillVariance ? 'Variance to Prior Year — HC / Non-HC split' : undefined}
                                    ><VarianceStack actual={totalActual} base={totalPy} /></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>

            {!effectiveCategory && (
                <div className="esd-section">
                    <div className="esd-section-head">
                        <span className="esd-section-icon">🏷️</span>
                        <span className="esd-section-title">Category Breakdown</span>
                    </div>
                    <div className="esd-table-wrap">
                        <table className="esd-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Category</th>
                                    <th>Actual/Forecast</th>
                                    <th>AOP</th>
                                    <th>Prior Year</th>
                                    <th>Var. to AOP</th>
                                    <th>Var. to PY</th>
                                    <th>Run Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categoryRows.map(row => (
                                    <tr
                                        key={row.category}
                                        style={{ cursor: onDrillCategory ? 'pointer' : 'default' }}
                                        onClick={() => onDrillCategory?.(row.category)}
                                        title={onDrillCategory ? `Drill into ${row.category}` : undefined}
                                    >
                                        <td style={{ textAlign: 'left' }}>{row.category}</td>
                                        <td>{fmtMoney(row.actual)}</td>
                                        <td>{fmtMoney(row.aop)}</td>
                                        <td>{fmtMoney(row.py)}</td>
                                        <td><VarianceStack actual={row.actual} base={row.aop} /></td>
                                        <td><VarianceStack actual={row.actual} base={row.py} /></td>
                                        <td
                                            className={onDrillRunRate ? 'esd-cell-click' : undefined}
                                            onClick={e => { e.stopPropagation(); onDrillRunRate?.(row.category); }}
                                            title={onDrillRunRate ? `Run Rate — ${row.category}` : undefined}
                                        >
                                            {onDrillRunRate ? '📈 View' : '—'}
                                        </td>
                                    </tr>
                                ))}
                                {!categoryRows.length && (
                                    <tr><td colSpan={7} className="esd-empty-row">No category data available.</td></tr>
                                )}
                            </tbody>
                            {!!categoryRows.length && (
                                <tfoot>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                        <td
                                            className={onDrillTotal ? 'esd-cell-click' : undefined}
                                            onClick={() => onDrillTotal?.('actual', scopeTotalRow)}
                                            title={onDrillTotal ? 'Total Actual/Forecast — HC / Non-HC split' : undefined}
                                        ><strong>{fmtMoney(totalActual)}</strong></td>
                                        <td
                                            className={onDrillTotal ? 'esd-cell-click' : undefined}
                                            onClick={() => onDrillTotal?.('aop', scopeTotalRow)}
                                            title={onDrillTotal ? 'Total AOP — HC / Non-HC split' : undefined}
                                        ><strong>{fmtMoney(totalAop)}</strong></td>
                                        <td
                                            className={onDrillTotal ? 'esd-cell-click' : undefined}
                                            onClick={() => onDrillTotal?.('py', scopeTotalRow)}
                                            title={onDrillTotal ? 'Total Prior Year — HC / Non-HC split' : undefined}
                                        ><strong>{fmtMoney(totalPy)}</strong></td>
                                        <td
                                            className={onDrillVariance ? 'esd-cell-click' : undefined}
                                            onClick={() => onDrillVariance?.('var_aop', scopeTotalRow)}
                                            title={onDrillVariance ? 'Total Variance to AOP — HC / Non-HC split' : undefined}
                                        ><VarianceStack actual={totalActual} base={totalAop} /></td>
                                        <td
                                            className={onDrillVariance ? 'esd-cell-click' : undefined}
                                            onClick={() => onDrillVariance?.('var_py', scopeTotalRow)}
                                            title={onDrillVariance ? 'Total Variance to Prior Year — HC / Non-HC split' : undefined}
                                        ><VarianceStack actual={totalActual} base={totalPy} /></td>
                                        <td
                                            className={onDrillRunRate ? 'esd-cell-click' : undefined}
                                            onClick={e => { e.stopPropagation(); onDrillRunRate?.(null); }}
                                            title={onDrillRunRate ? 'Run Rate — Total' : undefined}
                                        >
                                            {onDrillRunRate ? '📈 View' : '—'}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            )}
        </DrillModalShell>
    );
}

// Block name that monthly_values is keyed by for a given column group and
// year — "actual_fcst_<year>" (Actual/Forecast column), "aop_<year>" (AOP
// column), "actual_<year>" (Prior Year column, keyed by ITS OWN year, e.g.
// the 2025 block for a 2026 dashboard). Matches expense_fiscal_year.py's
// naming convention on the backend.
const KIND_LABEL = { actual: 'Actual/Forecast', aop: 'AOP', py: 'Prior Year', var_aop: 'Variance to AOP', var_py: 'Variance to Prior Year' };

const SOURCE_ROWS = [
    { source: 'hc', label: 'Headcount', sub: 'At employee level' },
    { source: 'nonhc', label: 'Non-Headcount', sub: 'At vendor / expense category level' },
];

function MonthSplitModal({ monthLabel, hc, nonhc, breadcrumb, onJump, onClose, onDrillCell }) {
    // A source with nothing in Actual/AOP/PY for this month/scope (e.g. the
    // Category filter narrowed to the other source) is dropped -- just
    // noise, same as every other category/department table across the app.
    const rows = SOURCE_ROWS
        .map(r => ({ ...r, ...(r.source === 'hc' ? hc : nonhc) }))
        .filter(r => r.actual || r.aop || r.py);
    const totalActual = hc.actual + nonhc.actual;
    const totalAop = hc.aop + nonhc.aop;
    const totalPy = hc.py + nonhc.py;

    return (
        <DrillModalShell
            icon="🔎" title={monthLabel} sub="Headcount vs. Non-Headcount breakdown"
            maxWidth={760}
            breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}
        >
            <div className="esd-table-wrap">
                <table className="esd-table esd-table-cellclick">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Source</th>
                            <th>Actual/Forecast</th>
                            <th>AOP</th>
                            <th>Prior Year</th>
                            <th>Var AOP and %</th>
                            <th>Var PY and %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.source}>
                                <td style={{ textAlign: 'left' }}>
                                    <div>{r.label}</div>
                                    <div style={{ fontSize: '0.82em', fontWeight: 500, color: 'var(--esd-muted)' }}>{r.sub}</div>
                                </td>
                                <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'actual')} title={`${r.label} employees/items — ${monthLabel}`}>
                                    <strong>{fmtMoney(r.actual)}</strong>
                                </td>
                                <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'aop')} title={`${r.label} AOP — ${monthLabel}`}>
                                    <strong>{fmtMoney(r.aop)}</strong>
                                </td>
                                <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'py')} title={`${r.label} Prior Year — ${monthLabel}`}>
                                    <strong>{fmtMoney(r.py)}</strong>
                                </td>
                                <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'var_aop')} title={`${r.label} Variance to AOP — ${monthLabel}`}>
                                    <VarianceStack actual={r.actual} base={r.aop} />
                                </td>
                                <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'var_py')} title={`${r.label} Variance to Prior Year — ${monthLabel}`}>
                                    <VarianceStack actual={r.actual} base={r.py} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="esd-cell-click" onClick={() => onDrillCell('total', 'actual')}>
                            <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                            <td><strong>{fmtMoney(totalActual)}</strong></td>
                            <td><strong>{fmtMoney(totalAop)}</strong></td>
                            <td><strong>{fmtMoney(totalPy)}</strong></td>
                            <td><VarianceStack actual={totalActual} base={totalAop} /></td>
                            <td><VarianceStack actual={totalActual} base={totalPy} /></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </DrillModalShell>
    );
}

// Run Rate's own drill layer — Current / Dec AOP / Dec FCST Run Rate, split
// Headcount vs. Non-Headcount, for whichever scope (page-level Total, or
// one department/category) it was opened from. Clicking Headcount goes
// straight to employee-level records; clicking Non-Headcount goes to the
// per-category Run Rate list UNLESS this layer is already scoped to one
// category, in which case it goes straight to that category's vendor-level
// records — same one-hop depth as Headcount once a category is known.
function RunRateSplitModal({ label, sub, hc, nonhc, decLabel, breadcrumb, onJump, onClose, onDrillHc, onDrillNonhc, onDrillTotal }) {
    // A source with nothing in Current/Dec AOP/Dec FCST Run Rate for this
    // scope is dropped — just noise, same as every other category/department
    // table across the app.
    const rows = [
        { source: 'hc', title: 'Headcount', subLabel: 'At employee level', ...hc },
        { source: 'nonhc', title: 'Non-Headcount', subLabel: 'At vendor / expense category level', ...nonhc },
    ].filter(r => r.current || r.dec_aop || r.dec_fcst);
    const total = sumRunRates([hc, nonhc]);

    return (
        <DrillModalShell icon="🔁" title={label} sub={sub} maxWidth={860} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}>
            <div className="esd-table-wrap">
                <table className="esd-table">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Source</th>
                            <th>Current Run Rate</th>
                            <th>Dec{decLabel} AOP Run Rate</th>
                            <th>Dec{decLabel} FCST Run Rate</th>
                            <th>Var to AOP RR and %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.source} onClick={() => (r.source === 'hc' ? onDrillHc() : onDrillNonhc())}>
                                <td style={{ textAlign: 'left' }}>
                                    <div>{r.title}</div>
                                    <div style={{ fontSize: '0.82em', fontWeight: 500, color: 'var(--esd-muted)' }}>{r.subLabel}</div>
                                </td>
                                <td><strong>{fmtMoney(r.current)}</strong></td>
                                <td><strong>{fmtMoney(r.dec_aop)}</strong></td>
                                <td><strong>{fmtMoney(r.dec_fcst)}</strong></td>
                                <td><VarianceStack actual={r.dec_fcst} base={r.dec_aop} /></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr onClick={onDrillTotal} style={onDrillTotal ? { cursor: 'pointer' } : undefined} title={onDrillTotal ? 'Headcount + Non-Headcount combined — click for the breakdown' : undefined}>
                            <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                            <td><strong>{fmtMoney(total.current)}</strong></td>
                            <td><strong>{fmtMoney(total.dec_aop)}</strong></td>
                            <td><strong>{fmtMoney(total.dec_fcst)}</strong></td>
                            <td><VarianceStack actual={total.dec_fcst} base={total.dec_aop} /></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </DrillModalShell>
    );
}

// Middle layer, only ever reached from the page-level (unscoped) Run Rate
// split's Headcount or Non-Headcount row — every category's own Run Rate
// for that source, so which category is driving the company-wide number is
// one click away before going all the way down to its employees/vendors.
// Same view for both sources (source prop only changes title/copy/empty-row
// text) — HC used to skip straight to the employee list with no
// category-level stop, unlike Non-HC.
function RunRateCategoriesModal({ rows, source, decLabel, breadcrumb, onJump, onClose, onDrillCategory }) {
    const total = sumRunRates(rows);
    const sourceLabel = source === 'total' ? 'Total' : source === 'hc' ? 'Headcount' : 'Non-Headcount';
    const detailNoun = source === 'total' ? 'employee/vendor' : source === 'hc' ? 'employee' : 'vendor';

    return (
        <DrillModalShell
            icon="🔁" title={`${sourceLabel} Run Rate`} sub={`By expense category — click a category for ${detailNoun}-level detail`}
            maxWidth={860} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}
        >
            <div className="esd-table-wrap">
                <table className="esd-table">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Category</th>
                            <th>Current Run Rate</th>
                            <th>Dec{decLabel} AOP Run Rate</th>
                            <th>Dec{decLabel} FCST Run Rate</th>
                            <th>Var to AOP RR and %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.category} onClick={() => onDrillCategory(r.category)}>
                                <td style={{ textAlign: 'left' }}>{r.category}</td>
                                <td>{fmtMoney(r.current)}</td>
                                <td>{fmtMoney(r.dec_aop)}</td>
                                <td>{fmtMoney(r.dec_fcst)}</td>
                                <td><VarianceStack actual={r.dec_fcst} base={r.dec_aop} /></td>
                            </tr>
                        ))}
                        {!rows.length && (
                            <tr><td colSpan={5} className="esd-empty-row">No {sourceLabel} categories in this scope.</td></tr>
                        )}
                    </tbody>
                    {!!rows.length && (
                        <tfoot>
                            <tr>
                                <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                <td><strong>{fmtMoney(total.current)}</strong></td>
                                <td><strong>{fmtMoney(total.dec_aop)}</strong></td>
                                <td><strong>{fmtMoney(total.dec_fcst)}</strong></td>
                                <td><VarianceStack actual={total.dec_fcst} base={total.dec_aop} /></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </DrillModalShell>
    );
}

// Deepest Run Rate layer — every employee (HC) or vendor/line-item (Non-HC)
// behind the row that was clicked, each annualized the same way as the
// summary above it (last actual month × 12, December AOP × 12, December
// forecast × 12) rather than a plain monthly/YTD sum.
function RunRateRecordsModal({ source, category, lastActualMonth, decMonth, currentYear, records, loading, error, breadcrumb, onJump, onClose }) {
    const isHc = source === 'hc';
    // 'total' mixes employee (HC) and vendor/line-item (Non-HC) records in
    // one list — recordsFor('total') already tags each record with its own
    // _source, so name/sub-category/icon are decided per-record instead of
    // once for the whole modal the way the single-source views do.
    const isMixed = source === 'total';
    const scoped = (records || []).filter(r => !category || (r.category || 'Unspecified') === category);
    const employeeFilters = useEmployeeDetailFilters(scoped, source);
    const valued = employeeFilters.records.map(r => {
        const recordIsHc = isMixed ? r._source === 'hc' : isHc;
        return {
            ...r,
            _isHc: recordIsHc,
            _current: r.run_rate?.current,
            _decAop: r.run_rate?.dec_aop,
            _decFcst: r.run_rate?.dec_fcst,
            _name: recordIsHc ? (r.headcount_name_actual || r.headcount_name_aop || '') : (r.name || ''),
            _sub: recordIsHc ? (r.status || '') : (r.sub_category || ''),
        };
    });
    const sorted = [...valued].sort((a, b) => b._current - a._current);
    const totals = sumRunRates(valued.map(r => r.run_rate));

    return (
        <DrillModalShell
            icon={isMixed ? '🔁' : isHc ? '👥' : '📦'}
            title={`${isMixed ? 'Employees & Non-HC Items' : isHc ? 'Employees' : 'Non-HC Items'}${category ? ` — ${category}` : ''}`}
            sub="Run Rate by record - OTE, full-year amounts and configured three-month averages"
            maxWidth={900}
            breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}
        >
            {!loading && !error && employeeFilters.controls}
            {error && <p style={{ padding: '8px 0', color: '#dc2626' }}>{error}</p>}
            {!error && (
                <div className="esd-table-wrap">
                    <table className="esd-table esd-table-static">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>{isMixed ? 'Employee / Name' : isHc ? 'Employee' : 'Name'}</th>
                                <th style={{ textAlign: 'left' }}>{isMixed ? 'Status / Sub-Category' : isHc ? 'Status' : 'Sub-Category'}</th>
                                <th>Current Run Rate</th>
                                <th>Dec AOP Run Rate</th>
                                <th>Dec FCST Run Rate</th>
                                <th>Var to AOP RR and %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={6} className="esd-empty-row">Loading…</td></tr>}
                            {!loading && sorted.map(r => (
                                <tr key={r.id}>
                                    <td style={{ textAlign: 'left' }}>{r._name || '—'}</td>
                                    <td style={{ textAlign: 'left' }}>{r._sub || '—'}</td>
                                    <td>{fmtMoney(r._current)}</td>
                                    <td>{fmtMoney(r._decAop)}</td>
                                    <td>{fmtMoney(r._decFcst)}</td>
                                    <td><VarianceStack actual={r._decFcst} base={r._decAop} /></td>
                                </tr>
                            ))}
                            {!loading && !sorted.length && (
                                <tr><td colSpan={6} className="esd-empty-row">No records in this scope.</td></tr>
                            )}
                        </tbody>
                        {!loading && !!sorted.length && (
                            <tfoot>
                                <tr>
                                    <td colSpan={2} style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                    <td><strong>{fmtMoney(totals.current)}</strong></td>
                                    <td><strong>{fmtMoney(totals.dec_aop)}</strong></td>
                                    <td><strong>{fmtMoney(totals.dec_fcst)}</strong></td>
                                    <td><VarianceStack actual={totals.dec_fcst} base={totals.dec_aop} /></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}
        </DrillModalShell>
    );
}

// Deepest drill layer — the individual employee (HC) or line-item (Non-HC)
// records behind one HC/Non-HC cell of the Monthly Detail table. Fetched
// once and cached; filtered/valued for the exact month + column clicked.
// Search narrows by name/category/status, and every column header sorts —
// value-descending by default so the biggest contributor always leads.
function RecordsListModal(props) {
    return <DrillModalShell icon="??" title={props.source === 'hc' ? 'Employees' : props.source === 'total' ? 'Employees and Vendors' : 'Expense Categories / Vendors'} sub={props.monthLabel} maxWidth={1100} breadcrumb={props.breadcrumb} onJump={props.onJump} onClose={props.onClose} onBack={props.onBack}>
        <GARecordDetails {...props} />
    </DrillModalShell>;
}

function MarketingSummaryDashboard({
    department = [], quarter = [], customRange = null, category: sourceFilter = [], yearMode = 'current', onYears,
    hcType = [], status = [], country = [], subCategory = [], vendorName = [], period = [],
}) {
    // The page-level KPI cards/tables/charts above are fully multi-select —
    // several Department Sub-Categories sum together, same as several
    // Countries or Statuses. The deepest nested drill-downs reached by
    // clicking further INTO an already-open popup (MonthSplitModal,
    // RecordsListModal, Run Rate record lists) fall back to just the first
    // selected Department Sub-Category instead of blending several, to keep
    // every number at that depth unambiguous rather than silently summing
    // unrelated categories' individual records together.
    const singleDepartment = department && department.length ? department[0] : null;
    const [data, setData] = useState(null);
    const [gaSummary, setGaSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [drill, setDrill] = useState(null);
    // RecordsListModal's own Non-HC "expense category" drill-within-a-drill
    // (see GARecordDetails) — lifted up here instead of living as local
    // state inside the modal, so the modal header's own back arrow can pop
    // whichever level is active (vendor list -> category list -> outer
    // breadcrumb level), same one-hop-back pattern this department's own
    // HC & Non-HC 360 records view already has.
    const [expense, setExpense] = useState(null);
    const [showItems, setShowItems] = useState(false);
    useEffect(() => { setExpense(null); setShowItems(false); }, [drill]);
    const [pctDrill, setPctDrill] = useState(null);
    const [hcRecords, setHcRecords] = useState(null);
    const [hcRecordsLoading, setHcRecordsLoading] = useState(false);
    const [hcRecordsError, setHcRecordsError] = useState('');
    const [nonhcRecords, setNonhcRecords] = useState(null);
    const [nonhcRecordsLoading, setNonhcRecordsLoading] = useState(false);
    const [nonhcRecordsError, setNonhcRecordsError] = useState('');

    const filteredHc = filterRecords(hcRecords, 'hc', { hcType, status, country });
    const filteredNonhc = filterRecords(nonhcRecords, 'nonhc', { hcType, status, country, subCategory, vendorName });
    const recordsFor = source => source === 'total'
        ? [...filteredHc.map(r => ({ ...r, _source: 'hc' })), ...filteredNonhc.map(r => ({ ...r, _source: 'nonhc' }))]
        : source === 'hc' ? filteredHc : filteredNonhc;
    useEffect(() => { setDrill(null); }, [department, quarter, customRange, yearMode, hcType, status, country, subCategory, vendorName, period]);

    // Status/HC Type/Country/Sub-Category are applied server-side (unlike
    // Department/Quarter/Year, which reshape the already-fetched monthly
    // series client-side) — see summary_dashboard.py's comment — so changing
    // any of them here means a fresh fetch, not just a client-side reshape.
    const query = useMemo(() => {
        const params = new URLSearchParams();
        if (hcType.length) params.set('hc_type', hcType.join(','));
        if (status.length) params.set('status', status.join(','));
        if (country.length) params.set('country', country.join(','));
        if (subCategory.length) params.set('sub_category', subCategory.join(','));
        if (vendorName.length) params.set('name', vendorName.join(','));
        return params.toString();
    }, [hcType, status, country, subCategory, vendorName]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        (async () => {
            try {
                const qs = query ? `?${query}` : '';
                const [dashRes, summaryRes] = await Promise.all([
                    fetch(`${API_URL}/api/marketing/summary-dashboard/${qs}`, { credentials: 'include' }),
                    fetch(`${API_URL}/api/marketing/summary/${qs}`, { credentials: 'include' }),
                ]);
                const dashBody = await readApiJson(dashRes, 'Could not load the summary dashboard.');
                if (!cancelled) setData(dashBody);
                try {
                    const summaryBody = await readApiJson(summaryRes, 'Could not load the G&A summary.');
                    if (!cancelled) setGaSummary(summaryBody);
                } catch {
                    // Department Highlights is a bonus section — don't fail the whole page over it.
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [query]);

    // Reports the raw fiscal years up to the global filter bar (once known)
    // so its Year dropdown can show real labels ("FY2026") instead of
    // generic ones — must stay above the loading/error early returns below,
    // since hooks can't be called conditionally.
    const rawCurrentYear = data?.monthly_table?.[0]?.month.slice(0, 4) || '';
    const rawPriorYear = rawCurrentYear ? String(Number(rawCurrentYear) - 1) : '';
    useEffect(() => {
        if (rawCurrentYear) onYears?.({ current: rawCurrentYear, prior: rawPriorYear });
    }, [rawCurrentYear, rawPriorYear, onYears]);

    if (loading) return <div className="esd-state">Loading summary dashboard…</div>;
    if (error) return <div className="esd-state">{error}</div>;
    if (!data?.has_data) return <div className="esd-state">{data?.message || 'No data available yet — upload the Headcount Master and/or Non-HC workbooks.'}</div>;

    const effectiveData = buildScopedData(data, gaSummary, department, quarter, customRange, sourceFilter, yearMode, period);

    // The raw fiscal year never changes with the Year filter (month keys
    // stay literal); only which one is DISPLAYED as "current" swaps, and the
    // prior year has no further comparison data behind it so it's blank
    // when already viewing it.
    const selectedYearModes = Array.isArray(yearMode) ? yearMode : (yearMode ? [yearMode] : []);
    const isPriorOnlyView = selectedYearModes.includes('prior') && !selectedYearModes.includes('current');
    const currentYear = isPriorOnlyView ? rawPriorYear : rawCurrentYear;
    const priorYear = isPriorOnlyView ? '' : rawPriorYear;

    const ytdCount = effectiveData.variance_to_aop_monthly.length;
    const ytdRows = effectiveData.monthly_table.slice(0, ytdCount);
    const seriesValues = (rows, key) => Object.fromEntries(rows.map(r => [r.month, r[key]]));

    const chart1Groups = ytdRows.map(r => ({ key: r.month, label: r.label }));
    const chart1Series = [
        { name: `Actual ${currentYear}`, color: '#0e7490', values: seriesValues(ytdRows, 'total_actual') },
        { name: `AOP ${currentYear}`, color: '#1e3a5f', values: seriesValues(ytdRows, 'total_aop') },
        { name: `PY ${priorYear} Actuals`, color: '#cbd5e1', values: seriesValues(ytdRows, 'total_py') },
    ];

    const chart2Groups = effectiveData.monthly_table.map(r => ({ key: r.month, label: r.label }));
    const chart2Stack = [
        { name: `HC Cost Act/Fcst ${currentYear}`, color: '#5eead4', values: seriesValues(effectiveData.monthly_table, 'hc_actual') },
        { name: `Non-HC Costs Act/Fcst ${currentYear}`, color: '#0f766e', values: seriesValues(effectiveData.monthly_table, 'nonhc_actual') },
    ];
    const chart2Lines = [
        { name: `AOP ${currentYear}`, color: '#f59e0b', values: seriesValues(effectiveData.monthly_table, 'total_aop') },
        { name: `PY ${priorYear} Actuals`, color: '#1e293b', dashed: true, values: seriesValues(effectiveData.monthly_table, 'total_py') },
    ];

    const allMonths = effectiveData.monthly_table.map(r => r.month);

    // Card/section labels name the actual filter selection instead of a
    // generic "Selected Period" — a Quarter/YTD/Custom-Range pick should say
    // so, not read the same as the Full Year default. `isFullYear` also
    // decides whether "+ Forecast" belongs in the Actuals label: only the
    // unbounded full-year view can contain not-yet-actual future months.
    const monthAbbrev = iso => fmtMonthLabel(iso).replace(/ \d{4}$/, '');
    const periodIsYtdOnly = period.includes('ytd') && !period.includes('fy');
    const isFullYear = !quarter.length && !customRange && !periodIsYtdOnly;
    // YTD names the actual as-of month it runs through (YTD-Aug, YTD-Sep,
    // ...) instead of a bare "YTD" that doesn't say how much of the year is
    // actually included.
    const ytdThroughAbbrev = monthAbbrev(data.as_of);
    const quarterLabel = quarter.join('+');
    const periodTag = customRange
        ? `${monthAbbrev(customRange.start)}–${monthAbbrev(customRange.end)}`
        : quarter.length
            ? quarterLabel
            : periodIsYtdOnly
                ? `YTD-${ytdThroughAbbrev}`
                : `FY${String(currentYear).slice(-2)}`;
    const periodTitle = customRange
        ? `${fmtMonthLabel(customRange.start)} – ${fmtMonthLabel(customRange.end)}`
        : quarter.length
            ? `${quarterLabel} ${currentYear}`
            : periodIsYtdOnly
                ? `YTD through ${fmtMonthLabel(data.as_of)}`
                : `${currentYear} Full Year`;

    // G&A Total spend as a % of Revenue/ARR — both null (shown as nothing,
    // not 0%) when the uploaded workbook had no G&A_Summary sheet with
    // these rows. Revenue is a flow (summed across the scoped months, same
    // as every other dollar figure here); Ending ARR is a balance (the last
    // month in scope), see scopedMetricValue's own comment.
    const revenueArr = data.revenue_arr || {};
    const scopeOptions = { quarter, customRange, period, asOf: data.as_of, yearMode, priorYear };
    const revenueActualScoped = scopedMetricValue(revenueArr.revenue, 'actual', scopeOptions);
    const revenueAopScoped = scopedMetricValue(revenueArr.revenue, 'aop', scopeOptions);
    const revenuePyScoped = scopedMetricValue(revenueArr.revenue, 'py', scopeOptions);
    const arrActualScoped = scopedMetricValue(revenueArr.ending_arr, 'actual', scopeOptions);
    const arrAopScoped = scopedMetricValue(revenueArr.ending_arr, 'aop', scopeOptions);
    const arrPyScoped = scopedMetricValue(revenueArr.ending_arr, 'py', scopeOptions);
    const pctOfRevenueActual = pctOfMetric(effectiveData.full_year.actual_forecast, revenueActualScoped);
    const pctOfArrActual = pctOfMetric(effectiveData.full_year.actual_forecast, arrActualScoped);
    const pctOfRevenueAop = pctOfMetric(effectiveData.full_year.aop, revenueAopScoped);
    const pctOfArrAop = pctOfMetric(effectiveData.full_year.aop, arrAopScoped);
    const pctOfRevenuePriorYear = pctOfMetric(effectiveData.full_year.prior_year, revenuePyScoped);
    const pctOfArrPriorYear = pctOfMetric(effectiveData.full_year.prior_year, arrPyScoped);
    // The displayed number IS the same ratio-change the color is based on —
    // Actual % of Revenue/ARR minus AOP % of Revenue/ARR, in percentage
    // points — not a $ variance divided by revenue. Those used to be
    // computed separately (color from the ratio comparison, value from the
    // $ variance ÷ actual revenue), which could show e.g. a red card with a
    // negative-looking number whenever Actual and AOP Revenue/ARR differed,
    // since dividing the SAME $ variance by two different revenue bases
    // doesn't reduce to the honest difference between the two ratios.
    const pctOfRevenueVariance = pctOfRevenueActual != null && pctOfRevenueAop != null ? pctOfRevenueActual - pctOfRevenueAop : null;
    const pctOfArrVariance = pctOfArrActual != null && pctOfArrAop != null ? pctOfArrActual - pctOfArrAop : null;
    const revenueRatioWorsened = pctOfRevenueVariance != null && pctOfRevenueVariance > 0;
    const arrRatioWorsened = pctOfArrVariance != null && pctOfArrVariance > 0;
    const pctOfRevenuePyVariance = pctOfMetric(effectiveData.full_year.actual_forecast - effectiveData.full_year.prior_year, revenueActualScoped);
    const pctOfArrPyVariance = pctOfMetric(effectiveData.full_year.actual_forecast - effectiveData.full_year.prior_year, arrActualScoped);

    // Current Run Rate is always an annualized, full-year-equivalent figure
    // — so its own % of Revenue/ARR must divide by the TRUE full-year
    // Revenue/ARR too, not whatever quarter/YTD/custom-range filter is
    // active on the rest of the page. Marketing's source workbook has no
    // Revenue/ARR data at all (confirmed: MKTG_Sum has no Ending ARR/
    // Revenue rows, and the Dashboard sheet's own "YTD Cost to Revenue
    // Ratio" cell is a broken #REF! in the source file) — so revenueArr is
    // always {} here and both percentages below resolve to null, rendering
    // as "N/A". Kept wired up (not stripped) so these two cards light up
    // automatically the moment a workbook with real Revenue/ARR data is
    // uploaded, with no code change needed.
    const fyScopeOptions = { quarter: '', customRange: null, period: 'fy', asOf: data.as_of, yearMode, priorYear };
    const revenueActualFY = scopedMetricValue(revenueArr.revenue, 'actual', fyScopeOptions);
    const arrActualFY = scopedMetricValue(revenueArr.ending_arr, 'actual', fyScopeOptions);
    const revenueAopFY = scopedMetricValue(revenueArr.revenue, 'aop', fyScopeOptions);
    const arrAopFY = scopedMetricValue(revenueArr.ending_arr, 'aop', fyScopeOptions);
    const pctOfRevenueRunRate = pctOfMetric(effectiveData.run_rate.current, revenueActualFY);
    const pctOfArrRunRate = pctOfMetric(effectiveData.run_rate.current, arrActualFY);
    const pctOfRevenueDecAop = pctOfMetric(effectiveData.run_rate.dec_aop, revenueAopFY);
    const pctOfArrDecAop = pctOfMetric(effectiveData.run_rate.dec_aop, arrAopFY);
    const pctOfRevenueDecFcst = pctOfMetric(effectiveData.run_rate.dec_fcst, revenueActualFY);
    const pctOfArrDecFcst = pctOfMetric(effectiveData.run_rate.dec_fcst, arrActualFY);

    const pctVal = v => (v != null ? `${v.toFixed(1)}%` : 'N/A');
    // Every % of Revenue / % of ARR card opens the same breakdown — all 4
    // (Snapshot) or 3 (Run Rate) related percentages for whichever metric
    // was clicked, as the same KpiCard tiles the page itself uses.
    const snapshotRevenueCards = [
        { tone: 'b', icon: '💹', label: `${periodTag} Actuals % of Revenue`, value: pctVal(pctOfRevenueActual), sub: `Actuals+Forecast vs. Revenue — ${periodTitle}`, formula: 'Actuals+Forecast ÷ Revenue × 100', numerator: effectiveData.full_year.actual_forecast, denominator: revenueActualScoped },
        { tone: 'b', icon: '💹', label: `${periodTag} AOP % of Revenue`, value: pctVal(pctOfRevenueAop), sub: `AOP Budget vs. AOP Revenue — ${periodTitle}`, formula: 'AOP Budget ÷ AOP Revenue × 100', numerator: effectiveData.full_year.aop, denominator: revenueAopScoped },
        { tone: 'b', icon: '💹', label: `${periodTag} Prior Year % of Revenue`, value: pctVal(pctOfRevenuePriorYear), sub: priorYear ? `FY${priorYear.slice(2)} Actuals vs. FY${priorYear.slice(2)} Revenue` : 'No comparison year available', formula: 'FY Prior Year Actuals ÷ FY Prior Year Revenue × 100', numerator: effectiveData.full_year.prior_year, denominator: revenuePyScoped },
        { tone: revenueRatioWorsened ? 'bad' : 'good', icon: revenueRatioWorsened ? '⚠️' : '✅', label: 'vs. AOP Variance % of Revenue', value: pctVal(pctOfRevenueVariance), sub: `vs. AOP Variance vs. Revenue — ${periodTitle}`, formula: 'Actuals % of Revenue − AOP % of Revenue', minuendPct: pctOfRevenueActual, subtrahendPct: pctOfRevenueAop },
        { tone: pctOfRevenuePyVariance <= 0 ? 'good' : 'bad', icon: pctOfRevenuePyVariance <= 0 ? '✅' : '⚠️', label: 'vs. PY Variance % of Revenue', value: pctVal(pctOfRevenuePyVariance), sub: `vs. PY Variance vs. Revenue — ${periodTitle}`, formula: 'vs. PY Variance ÷ Revenue × 100', numerator: effectiveData.full_year.actual_forecast - effectiveData.full_year.prior_year, denominator: revenueActualScoped },
    ];
    const snapshotArrCards = [
        { tone: 'c', icon: '📡', label: `${periodTag} Actuals % of ARR`, value: pctVal(pctOfArrActual), sub: `Actuals+Forecast vs. Ending ARR — ${periodTitle}`, formula: 'Actuals+Forecast ÷ Ending ARR × 100', numerator: effectiveData.full_year.actual_forecast, denominator: arrActualScoped },
        { tone: 'c', icon: '📡', label: `${periodTag} AOP % of ARR`, value: pctVal(pctOfArrAop), sub: `AOP Budget vs. AOP Ending ARR — ${periodTitle}`, formula: 'AOP Budget ÷ AOP Ending ARR × 100', numerator: effectiveData.full_year.aop, denominator: arrAopScoped },
        { tone: 'c', icon: '📡', label: `${periodTag} Prior Year % of ARR`, value: pctVal(pctOfArrPriorYear), sub: priorYear ? `FY${priorYear.slice(2)} Actuals vs. FY${priorYear.slice(2)} Ending ARR` : 'No comparison year available', formula: 'FY Prior Year Actuals ÷ FY Prior Year Ending ARR × 100', numerator: effectiveData.full_year.prior_year, denominator: arrPyScoped },
        { tone: arrRatioWorsened ? 'bad' : 'good', icon: arrRatioWorsened ? '⚠️' : '✅', label: 'vs. AOP Variance % of ARR', value: pctVal(pctOfArrVariance), sub: `vs. AOP Variance vs. Ending ARR — ${periodTitle}`, formula: 'Actuals % of ARR − AOP % of ARR', minuendPct: pctOfArrActual, subtrahendPct: pctOfArrAop },
        { tone: pctOfArrPyVariance <= 0 ? 'good' : 'bad', icon: pctOfArrPyVariance <= 0 ? '✅' : '⚠️', label: 'vs. PY Variance % of ARR', value: pctVal(pctOfArrPyVariance), sub: `vs. PY Variance vs. Ending ARR — ${periodTitle}`, formula: 'vs. PY Variance ÷ Ending ARR × 100', numerator: effectiveData.full_year.actual_forecast - effectiveData.full_year.prior_year, denominator: arrActualScoped },
    ];
    const runRateRevenueCards = [
        { tone: 'b', icon: '💹', label: 'Current Run Rate % of Revenue', value: pctVal(pctOfRevenueRunRate), sub: `Current Run Rate vs. FY${currentYear.slice(2)} Revenue`, formula: 'Current Run Rate ÷ FY Revenue × 100', numerator: effectiveData.run_rate.current, denominator: revenueActualFY },
        { tone: 'b', icon: '💹', label: `Dec '${currentYear.slice(2)} AOP Run Rate % of Revenue`, value: pctVal(pctOfRevenueDecAop), sub: `Dec AOP Run Rate vs. FY${currentYear.slice(2)} AOP Revenue`, formula: 'Dec AOP Run Rate ÷ FY AOP Revenue × 100', numerator: effectiveData.run_rate.dec_aop, denominator: revenueAopFY },
        { tone: 'b', icon: '💹', label: `Dec '${currentYear.slice(2)} Fcst Run Rate % of Revenue`, value: pctVal(pctOfRevenueDecFcst), sub: `Dec Fcst Run Rate vs. FY${currentYear.slice(2)} Revenue`, formula: 'Dec Fcst Run Rate ÷ FY Revenue × 100', numerator: effectiveData.run_rate.dec_fcst, denominator: revenueActualFY },
    ];
    const runRateArrCards = [
        { tone: 'c', icon: '📡', label: 'Current Run Rate % of ARR', value: pctVal(pctOfArrRunRate), sub: `Current Run Rate vs. FY${currentYear.slice(2)} Ending ARR`, formula: 'Current Run Rate ÷ FY Ending ARR × 100', numerator: effectiveData.run_rate.current, denominator: arrActualFY },
        { tone: 'c', icon: '📡', label: `Dec '${currentYear.slice(2)} AOP Run Rate % of ARR`, value: pctVal(pctOfArrDecAop), sub: `Dec AOP Run Rate vs. FY${currentYear.slice(2)} AOP Ending ARR`, formula: 'Dec AOP Run Rate ÷ FY AOP Ending ARR × 100', numerator: effectiveData.run_rate.dec_aop, denominator: arrAopFY },
        { tone: 'c', icon: '📡', label: `Dec '${currentYear.slice(2)} Fcst Run Rate % of ARR`, value: pctVal(pctOfArrDecFcst), sub: `Dec Fcst Run Rate vs. FY${currentYear.slice(2)} Ending ARR`, formula: 'Dec Fcst Run Rate ÷ FY Ending ARR × 100', numerator: effectiveData.run_rate.dec_fcst, denominator: arrActualFY },
    ];

    // Monthly Detail (Snapshot groups only) + Category Breakdown (every
    // group) for the PctDeepDiveModal — same scope (allMonths, sourceFilter,
    // yearMode) and denominators already computed above for the headline %
    // cards, just applied per-month / per-category instead of once.
    const snapshotRevenueMonthly = monthlyPctRows(effectiveData.monthly_table, revenueArr.revenue, allMonths);
    const snapshotArrMonthly = monthlyPctRows(effectiveData.monthly_table, revenueArr.ending_arr, allMonths);
    const snapshotRevenueCategory = categoryPctRows(gaSummary, allMonths, sourceFilter, yearMode, revenueActualScoped, revenueAopScoped, revenuePyScoped);
    const snapshotArrCategory = categoryPctRows(gaSummary, allMonths, sourceFilter, yearMode, arrActualScoped, arrAopScoped, arrPyScoped);
    const runRateRevenueCategory = categoryRunRatePctRows(gaSummary, sourceFilter, yearMode, revenueActualFY, revenueAopFY, revenueActualFY);
    const runRateArrCategory = categoryRunRatePctRows(gaSummary, sourceFilter, yearMode, arrActualFY, arrAopFY, arrActualFY);
    const runRateCategoryHeaders = ['Current RR', 'Dec AOP RR', 'Dec Fcst RR', 'Var to AOP RR'];

    // "drill" holds the whole stack of open deep-dive layers, but only the
    // top of the stack is ever mounted — stacking multiple full-screen modal
    // overlays at once made a smaller/deeper modal fail to fully cover the
    // larger one behind it, so both were visible simultaneously ("duplicate
    // modal"). openDrill (from a KPI card/chart/etc.) starts a fresh stack;
    // drilling further from inside a modal (a month row, a category row)
    // pushes another layer and swaps the visible modal to it; the Back
    // button pops one layer instead of closing everything.
    const openDrill = (title, sub, months, category = null) => setDrill([{ type: 'dive', title, sub, months, category }]);
    const pushDrill = (title, sub, months, category = null) => setDrill(stack => [...stack, { type: 'dive', title, sub, months, category }]);
    const topDrill = drill && drill.length ? drill[drill.length - 1] : null;

    // One label per layer of the open drill stack, shown as a clickable
    // breadcrumb trail in the modal header — jumping to an earlier crumb
    // truncates the stack to that layer, same effect as repeatedly clicking
    // the old "← Back" button but in a single click from any depth.
    const breadcrumb = (drill || []).map(layer => {
        if (layer.type === 'records') return `${layer.source === 'hc' ? 'Employees' : 'Non-HC Items'} — ${layer.monthLabel}`;
        if (layer.type === 'split') return `HC / Non-HC breakdown — ${layer.monthLabel}`;
        if (layer.type === 'raterun') return layer.label;
        if (layer.type === 'raterun-categories') return `${layer.source === 'hc' ? 'Headcount' : 'Non-HC'} Run Rate — by category`;
        if (layer.type === 'raterun-records') return `${layer.source === 'hc' ? 'Employees' : 'Non-HC Items'} — Run Rate`;
        return layer.title;
    });
    const jumpDrill = index => setDrill(stack => stack.slice(0, index + 1));

    // Every Monthly Detail / Category Breakdown / KPI cell — whichever
    // column was clicked — opens the SAME full Common Points breakdown
    // (YTD Actual, AOP, PY, Var. to AOP, Var. to PY, each split HC vs.
    // Non-HC), since all of it comes from the one row's hc_*/nonhc_* fields
    // already carried on every Monthly Detail row and the KPI scope total.
    const pushSplit = (kind, row) => setDrill(stack => [...stack, {
        type: 'split', months: row._months || [row.month], monthLabel: row.label,
        hc: { actual: row.hc_actual || 0, aop: row.hc_aop || 0, py: row.hc_py || 0 },
        nonhc: { actual: row.nonhc_actual || 0, aop: row.nonhc_aop || 0, py: row.nonhc_py || 0 },
        category: topDrill?.category || singleDepartment || null,
    }]);

    // Employee/Non-HC line-item records are fetched once and cached — both
    // raw endpoints return every current record regardless of category or
    // month, so a single fetch covers every RecordsListModal drill. `months`
    // is an array — a single-month drill passes one, a YTD-scope drill (a
    // KPI card, a Category Breakdown Total) passes every month in scope, so
    // each record's value is summed the same way the KPI/table totals were.
    const ensureRecordsLoaded = source => {
        if (source === 'total') { ensureRecordsLoaded('hc'); ensureRecordsLoaded('nonhc'); return; }
        if (source === 'hc') {
            if (hcRecords || hcRecordsLoading) return;
            setHcRecordsLoading(true); setHcRecordsError('');
            fetch(`${API_URL}/api/marketing/raw/`, { credentials: 'include' })
                .then(res => readApiJson(res, 'Could not load employee records.'))
                .then(body => setHcRecords(body.records || []))
                .catch(err => setHcRecordsError(err.message))
                .finally(() => setHcRecordsLoading(false));
        } else {
            if (nonhcRecords || nonhcRecordsLoading) return;
            setNonhcRecordsLoading(true); setNonhcRecordsError('');
            fetch(`${API_URL}/api/marketing/nonhc/raw/`, { credentials: 'include' })
                .then(res => readApiJson(res, 'Could not load Non-HC records.'))
                .then(body => setNonhcRecords(body.records || []))
                .catch(err => setNonhcRecordsError(err.message))
                .finally(() => setNonhcRecordsLoading(false));
        }
    };

    const drillRecords = (source, kind, months, monthLabel) => {
        setDrill(stack => [...stack, {
            type: 'records', source, kind, months, monthLabel, category: (topDrill?.category || singleDepartment || null),
        }]);
        ensureRecordsLoaded(source);
    };

    // Run Rate — computed once for the page-level (Total) scope from
    // effectiveData.monthly_table; a department/category scope recomputes on
    // demand straight off gaSummary (categoryRunRateSplit), since Run Rate
    // needs the full 12-month series regardless of whatever `months` window
    // the surrounding view happens to be scoped to.
    const runRateSplitTotal = runRateSplit(gaSummary, department, sourceFilter, yearMode);
    const decLabel = ` '${currentYear.slice(2)}`;

    const buildRunRateLayer = (label, sub, category) => {
        if (!normalizeYearModesLocal(yearMode).includes('current')) return null;
        const split = category ? categoryRunRateSplit(gaSummary, category, sourceFilter, yearMode) : runRateSplitTotal;
        if (!split) return null;
        return {
            type: 'raterun', label, sub, category,
            hc: split.hc, nonhc: split.nonhc, lastActualMonth: split.lastActualMonth, decMonth: split.decMonth,
        };
    };
    const openRunRateSplit = (label, sub, category = null) => {
        const layer = buildRunRateLayer(label, sub, category);
        if (layer) setDrill([layer]);
    };
    const pushRunRateSplit = (label, sub, category) => {
        const layer = buildRunRateLayer(label, sub, category);
        if (layer) setDrill(stack => [...stack, layer]);
    };
    const pushRunRateCategories = (source) => {
        const rows = (gaSummary?.categories || [])
            .map(cat => {
                const split = categoryRunRateSplit(gaSummary, cat, sourceFilter, yearMode);
                if (!split) return null;
                // 'total' has no section of its own in the split — it's
                // Headcount + Non-Headcount combined for that one category,
                // same as the page-level Total row above this popup.
                return { category: cat, ...(source === 'total' ? sumRunRates([split.hc, split.nonhc]) : split[source]) };
            })
            .filter(row => row && (row.current || row.dec_aop || row.dec_fcst));
        setDrill(stack => [...stack, {
            type: 'raterun-categories', source, rows, lastActualMonth: topDrill.lastActualMonth, decMonth: topDrill.decMonth,
        }]);
    };
    const pushRunRateRecords = (source, category, lastActualMonth, decMonth) => {
        setDrill(stack => [...stack, { type: 'raterun-records', source, category, lastActualMonth, decMonth }]);
        ensureRecordsLoaded(source);
    };

    return (
        <div className="esd">
            {pctDrill && <PctDeepDiveModal {...pctDrill} onClose={() => setPctDrill(null)} />}
            {topDrill?.type === 'dive' && (
                <DeepDiveModal
                    title={topDrill.title} sub={topDrill.sub} months={topDrill.months} category={topDrill.category}
                    department={department} sourceFilter={sourceFilter} yearMode={yearMode} data={data} gaSummary={gaSummary}
                    breadcrumb={breadcrumb} onJump={jumpDrill}
                    onClose={() => setDrill(null)}
                    onDrillMonth={(monthKey, monthLabel) => pushDrill(
                        monthLabel, `${topDrill.category || singleDepartment ? `${topDrill.category || singleDepartment} — ` : ''}Category breakdown for ${monthLabel}`,
                        [monthKey], topDrill.category || singleDepartment || null,
                    )}
                    onDrillCategory={cat => pushDrill(cat, `Monthly detail for ${cat} — same period`, topDrill.months, cat)}
                    onDrillTotal={pushSplit}
                    onDrillVariance={pushSplit}
                    onDrillRunRate={cat => pushRunRateSplit(
                        cat ? `Run Rate — ${cat}` : 'Run Rate — Total',
                        cat ? `${cat} · annualized` : 'Company-wide · annualized',
                        cat || topDrill.category || singleDepartment || null,
                    )}
                />
            )}
            {topDrill?.type === 'split' && (
                <MonthSplitModal
                    monthLabel={topDrill.monthLabel}
                    hc={topDrill.hc} nonhc={topDrill.nonhc}
                    breadcrumb={breadcrumb} onJump={jumpDrill}
                    onClose={() => setDrill(null)}
                    onDrillCell={(source, kind) => drillRecords(source, kind, topDrill.months, topDrill.monthLabel)}
                />
            )}
            {topDrill?.type === 'raterun' && (
                <RunRateSplitModal
                    label={topDrill.label} sub={topDrill.sub} decLabel={decLabel}
                    hc={topDrill.hc} nonhc={topDrill.nonhc}
                    breadcrumb={breadcrumb} onJump={jumpDrill}
                    onClose={() => setDrill(null)}
                    onDrillHc={() => (topDrill.category
                        ? pushRunRateRecords('hc', topDrill.category, topDrill.lastActualMonth, topDrill.decMonth)
                        : pushRunRateCategories('hc'))}
                    onDrillNonhc={() => (topDrill.category
                        ? pushRunRateRecords('nonhc', topDrill.category, topDrill.lastActualMonth, topDrill.decMonth)
                        : pushRunRateCategories('nonhc'))}
                    onDrillTotal={() => (topDrill.category
                        ? pushRunRateRecords('total', topDrill.category, topDrill.lastActualMonth, topDrill.decMonth)
                        : pushRunRateCategories('total'))}
                />
            )}
            {topDrill?.type === 'raterun-categories' && (
                <RunRateCategoriesModal
                    rows={topDrill.rows} source={topDrill.source} decLabel={decLabel}
                    breadcrumb={breadcrumb} onJump={jumpDrill}
                    onClose={() => setDrill(null)}
                    onDrillCategory={cat => pushRunRateRecords(topDrill.source, cat, topDrill.lastActualMonth, topDrill.decMonth)}
                />
            )}
            {topDrill?.type === 'raterun-records' && (
                <RunRateRecordsModal
                    source={topDrill.source} category={topDrill.category}
                    lastActualMonth={topDrill.lastActualMonth} decMonth={topDrill.decMonth}
                    currentYear={currentYear}
                    records={recordsFor(topDrill.source)}
                    loading={topDrill.source === 'total' ? hcRecordsLoading || nonhcRecordsLoading : topDrill.source === 'hc' ? hcRecordsLoading : nonhcRecordsLoading}
                    error={topDrill.source === 'total' ? hcRecordsError || nonhcRecordsError : topDrill.source === 'hc' ? hcRecordsError : nonhcRecordsError}
                    breadcrumb={breadcrumb} onJump={jumpDrill}
                    onClose={() => setDrill(null)}
                />
            )}
            {topDrill?.type === 'records' && (
                <RecordsListModal
                    source={topDrill.source} kind={topDrill.kind} months={topDrill.months} monthLabel={topDrill.monthLabel}
                    category={topDrill.category} currentYear={currentYear} priorYear={priorYear}
                    records={recordsFor(topDrill.source)}
                    loading={topDrill.source === 'total' ? hcRecordsLoading || nonhcRecordsLoading : topDrill.source === 'hc' ? hcRecordsLoading : nonhcRecordsLoading}
                    error={topDrill.source === 'total' ? hcRecordsError || nonhcRecordsError : topDrill.source === 'hc' ? hcRecordsError : nonhcRecordsError}
                    expense={expense} onSelectExpense={setExpense}
                    showItems={showItems} onShowItems={setShowItems}
                    onBack={(expense || showItems) ? () => { if (showItems) setShowItems(false); else setExpense(null); } : undefined}
                    breadcrumb={breadcrumb} onJump={jumpDrill}
                    onClose={() => setDrill(null)}
                />
            )}

            <div className="esd-section">
                <div className="esd-section-head">
                    <span className="esd-section-icon">📈</span>
                    <span className="esd-section-title">{periodTitle} Snapshot</span>
                </div>
                <div className="esd-kpi-grid">
                    <KpiCard
                        tone="a" icon="💵" label={`${periodTag} Actuals${isFullYear ? ' + Forecast' : ''}`} value={fmtMoney(effectiveData.full_year.actual_forecast)}
                        sub={isFullYear ? 'Projected spend for the full year' : `Actual spend — ${periodTitle}`}
                        onClick={() => openDrill(`${periodTag} Actuals${isFullYear ? ' + Forecast' : ''}`, periodTitle, allMonths)}
                    />
                    <KpiCard
                        tone="b" icon="🎯" label={`${periodTag} AOP`} value={fmtMoney(effectiveData.full_year.aop)}
                        sub={`Budget — ${periodTitle}`}
                        onClick={() => openDrill(`${periodTag} AOP`, periodTitle, allMonths)}
                    />
                    <KpiCard
                        tone="c" icon="📅" label={`${periodTag} Prior Year`} value={fmtMoney(effectiveData.full_year.prior_year)} sub={priorYear ? `▲ ${effectiveData.full_year.yoy_growth_pct}% vs FY${priorYear.slice(2)}` : 'No comparison year available'}
                        onClick={() => openDrill(`${periodTag} Prior Year`, priorYear ? `${periodTitle}, ${priorYear}` : 'No comparison year available', allMonths)}
                    />
                    <VarianceKpi
                        label="vs. AOP Variance" amount={effectiveData.full_year.vs_aop.amount} pct={effectiveData.full_year.vs_aop.pct}
                        onClick={() => openDrill(`${periodTag} vs. AOP Variance`, periodTitle, allMonths)}
                    />
                    <VarianceKpi label="vs. PY Variance" amount={effectiveData.full_year.actual_forecast - effectiveData.full_year.prior_year}
                        pct={effectiveData.full_year.prior_year ? (effectiveData.full_year.actual_forecast - effectiveData.full_year.prior_year) / effectiveData.full_year.prior_year * 100 : null}
                        compareLabel="vs. PY"
                        onClick={() => openDrill(`${periodTag} vs. PY Variance`, priorYear ? `${periodTitle} vs. ${priorYear}` : 'No comparison year available', allMonths)} />
                </div>
                {/* % of Revenue and % of ARR each get their own grid row,
                    not folded into the row above — a shared auto-fit grid
                    would wrap all 13 cards by available width alone, mixing
                    dollar figures and percentages across rows with no
                    relationship between what lands next to what. */}
                <div className="esd-kpi-grid" style={{ marginTop: 8 }}>
                    <KpiCard
                        tone="b" icon="💹" label={`${periodTag} Actuals % of Revenue`} value={pctOfRevenueActual != null ? `${pctOfRevenueActual.toFixed(1)}%` : 'N/A'}
                        sub={`Actuals+Forecast vs. Revenue — ${periodTitle}`}
                        onClick={() => setPctDrill({ title: `${periodTag} % of Revenue Breakdown`, sub: periodTitle, cards: snapshotRevenueCards, monthlyRows: snapshotRevenueMonthly, categoryRows: snapshotRevenueCategory, metricLabel: 'Revenue' })}
                    />
                    <KpiCard
                        tone="b" icon="💹" label={`${periodTag} AOP % of Revenue`} value={pctOfRevenueAop != null ? `${pctOfRevenueAop.toFixed(1)}%` : 'N/A'}
                        sub={`AOP Budget vs. AOP Revenue — ${periodTitle}`}
                        onClick={() => setPctDrill({ title: `${periodTag} % of Revenue Breakdown`, sub: periodTitle, cards: snapshotRevenueCards, monthlyRows: snapshotRevenueMonthly, categoryRows: snapshotRevenueCategory, metricLabel: 'Revenue' })}
                    />
                    <KpiCard
                        tone="b" icon="💹" label={`${periodTag} Prior Year % of Revenue`} value={pctOfRevenuePriorYear != null ? `${pctOfRevenuePriorYear.toFixed(1)}%` : 'N/A'}
                        sub={priorYear ? `FY${priorYear.slice(2)} Actuals vs. FY${priorYear.slice(2)} Revenue` : 'No comparison year available'}
                        onClick={() => setPctDrill({ title: `${periodTag} % of Revenue Breakdown`, sub: periodTitle, cards: snapshotRevenueCards, monthlyRows: snapshotRevenueMonthly, categoryRows: snapshotRevenueCategory, metricLabel: 'Revenue' })}
                    />
                    <KpiCard
                        tone={revenueRatioWorsened ? 'bad' : 'good'} icon={revenueRatioWorsened ? '⚠️' : '✅'} label="vs. AOP Variance % of Revenue" value={pctOfRevenueVariance != null ? `${pctOfRevenueVariance.toFixed(1)}%` : 'N/A'} valueTone={revenueRatioWorsened ? 'bad' : 'good'} subTone={revenueRatioWorsened ? 'bad' : 'good'}
                        sub={`vs. AOP Variance vs. Revenue — ${periodTitle}`}
                        onClick={() => setPctDrill({ title: `${periodTag} % of Revenue Breakdown`, sub: periodTitle, cards: snapshotRevenueCards, monthlyRows: snapshotRevenueMonthly, categoryRows: snapshotRevenueCategory, metricLabel: 'Revenue' })}
                    />
                    <KpiCard
                        tone={pctOfRevenuePyVariance <= 0 ? 'good' : 'bad'} icon={pctOfRevenuePyVariance <= 0 ? '✅' : '⚠️'} label="vs. PY Variance % of Revenue" value={pctOfRevenuePyVariance != null ? `${pctOfRevenuePyVariance.toFixed(1)}%` : 'N/A'} valueTone={pctOfRevenuePyVariance <= 0 ? 'good' : 'bad'} subTone={pctOfRevenuePyVariance <= 0 ? 'good' : 'bad'}
                        sub={`vs. PY Variance vs. Revenue — ${periodTitle}`}
                        onClick={() => setPctDrill({ title: `${periodTag} % of Revenue Breakdown`, sub: periodTitle, cards: snapshotRevenueCards, monthlyRows: snapshotRevenueMonthly, categoryRows: snapshotRevenueCategory, metricLabel: 'Revenue' })}
                    />
                </div>
                <div className="esd-kpi-grid" style={{ marginTop: 8 }}>
                    <KpiCard
                        tone="c" icon="📡" label={`${periodTag} Actuals % of ARR`} value={pctOfArrActual != null ? `${pctOfArrActual.toFixed(1)}%` : 'N/A'}
                        sub={`Actuals+Forecast vs. Ending ARR — ${periodTitle}`}
                        onClick={() => setPctDrill({ title: `${periodTag} % of ARR Breakdown`, sub: periodTitle, cards: snapshotArrCards, monthlyRows: snapshotArrMonthly, categoryRows: snapshotArrCategory, metricLabel: 'Ending ARR' })}
                    />
                    <KpiCard
                        tone="c" icon="📡" label={`${periodTag} AOP % of ARR`} value={pctOfArrAop != null ? `${pctOfArrAop.toFixed(1)}%` : 'N/A'}
                        sub={`AOP Budget vs. AOP Ending ARR — ${periodTitle}`}
                        onClick={() => setPctDrill({ title: `${periodTag} % of ARR Breakdown`, sub: periodTitle, cards: snapshotArrCards, monthlyRows: snapshotArrMonthly, categoryRows: snapshotArrCategory, metricLabel: 'Ending ARR' })}
                    />
                    <KpiCard
                        tone="c" icon="📡" label={`${periodTag} Prior Year % of ARR`} value={pctOfArrPriorYear != null ? `${pctOfArrPriorYear.toFixed(1)}%` : 'N/A'}
                        sub={priorYear ? `FY${priorYear.slice(2)} Actuals vs. FY${priorYear.slice(2)} Ending ARR` : 'No comparison year available'}
                        onClick={() => setPctDrill({ title: `${periodTag} % of ARR Breakdown`, sub: periodTitle, cards: snapshotArrCards, monthlyRows: snapshotArrMonthly, categoryRows: snapshotArrCategory, metricLabel: 'Ending ARR' })}
                    />
                    <KpiCard
                        tone={arrRatioWorsened ? 'bad' : 'good'} icon={arrRatioWorsened ? '⚠️' : '✅'} label="vs. AOP Variance % of ARR" value={pctOfArrVariance != null ? `${pctOfArrVariance.toFixed(1)}%` : 'N/A'} valueTone={arrRatioWorsened ? 'bad' : 'good'} subTone={arrRatioWorsened ? 'bad' : 'good'}
                        sub={`vs. AOP Variance vs. Ending ARR — ${periodTitle}`}
                        onClick={() => setPctDrill({ title: `${periodTag} % of ARR Breakdown`, sub: periodTitle, cards: snapshotArrCards, monthlyRows: snapshotArrMonthly, categoryRows: snapshotArrCategory, metricLabel: 'Ending ARR' })}
                    />
                    <KpiCard
                        tone={pctOfArrPyVariance <= 0 ? 'good' : 'bad'} icon={pctOfArrPyVariance <= 0 ? '✅' : '⚠️'} label="vs. PY Variance % of ARR" value={pctOfArrPyVariance != null ? `${pctOfArrPyVariance.toFixed(1)}%` : 'N/A'} valueTone={pctOfArrPyVariance <= 0 ? 'good' : 'bad'} subTone={pctOfArrPyVariance <= 0 ? 'good' : 'bad'}
                        sub={`vs. PY Variance vs. Ending ARR — ${periodTitle}`}
                        onClick={() => setPctDrill({ title: `${periodTag} % of ARR Breakdown`, sub: periodTitle, cards: snapshotArrCards, monthlyRows: snapshotArrMonthly, categoryRows: snapshotArrCategory, metricLabel: 'Ending ARR' })}
                    />
                </div>
            </div>

            <div className="esd-section">
                <div className="esd-section-head">
                    <span className="esd-section-icon">🔁</span>
                    <span className="esd-section-title">Run Rate (Annualized)</span>
                </div>
                <div className="esd-kpi-grid">
                    <KpiCard
                        tone="d" icon="📈" label="Current Run Rate" value={fmtMoney(effectiveData.run_rate.current)} sub={"HC annual OTE + Non-HC expense rules"}
                        onClick={() => openRunRateSplit('Run Rate — Total', 'Company-wide · annualized')}
                    />
                    <KpiCard
                        tone="b" icon="🎯" label={`Dec '${currentYear.slice(2)} AOP Run Rate`} value={fmtMoney(effectiveData.run_rate.dec_aop)} sub={"December HC budget + Non-HC budget rules"}
                        onClick={() => openRunRateSplit('Run Rate — Total', 'Company-wide · annualized')}
                    />
                    <KpiCard
                        tone="a" icon="🔮" label={`Dec '${currentYear.slice(2)} Fcst Run Rate`} value={fmtMoney(effectiveData.run_rate.dec_fcst)} sub={"December HC OTE + Non-HC forecast rules"}
                        onClick={() => openRunRateSplit('Run Rate — Total', 'Company-wide · annualized')}
                    />
                </div>
                <div className="esd-kpi-grid" style={{ marginTop: 8 }}>
                    <KpiCard
                        tone="b" icon="💹" label="Current Run Rate % of Revenue" value={pctOfRevenueRunRate != null ? `${pctOfRevenueRunRate.toFixed(1)}%` : 'N/A'}
                        sub={`Current Run Rate vs. FY${currentYear.slice(2)} Revenue`}
                        onClick={() => setPctDrill({ title: 'Run Rate % of Revenue Breakdown', sub: 'Company-wide · annualized', cards: runRateRevenueCards, categoryRows: runRateRevenueCategory, categoryHeaders: runRateCategoryHeaders, metricLabel: 'Revenue' })}
                    />
                    <KpiCard
                        tone="b" icon="💹" label={`Dec '${currentYear.slice(2)} AOP Run Rate % of Revenue`} value={pctOfRevenueDecAop != null ? `${pctOfRevenueDecAop.toFixed(1)}%` : 'N/A'}
                        sub={`Dec AOP Run Rate vs. FY${currentYear.slice(2)} AOP Revenue`}
                        onClick={() => setPctDrill({ title: 'Run Rate % of Revenue Breakdown', sub: 'Company-wide · annualized', cards: runRateRevenueCards, categoryRows: runRateRevenueCategory, categoryHeaders: runRateCategoryHeaders, metricLabel: 'Revenue' })}
                    />
                    <KpiCard
                        tone="b" icon="💹" label={`Dec '${currentYear.slice(2)} Fcst Run Rate % of Revenue`} value={pctOfRevenueDecFcst != null ? `${pctOfRevenueDecFcst.toFixed(1)}%` : 'N/A'}
                        sub={`Dec Fcst Run Rate vs. FY${currentYear.slice(2)} Revenue`}
                        onClick={() => setPctDrill({ title: 'Run Rate % of Revenue Breakdown', sub: 'Company-wide · annualized', cards: runRateRevenueCards, categoryRows: runRateRevenueCategory, categoryHeaders: runRateCategoryHeaders, metricLabel: 'Revenue' })}
                    />
                </div>
                <div className="esd-kpi-grid" style={{ marginTop: 8 }}>
                    <KpiCard
                        tone="c" icon="📡" label="Current Run Rate % of ARR" value={pctOfArrRunRate != null ? `${pctOfArrRunRate.toFixed(1)}%` : 'N/A'}
                        sub={`Current Run Rate vs. FY${currentYear.slice(2)} Ending ARR`}
                        onClick={() => setPctDrill({ title: 'Run Rate % of ARR Breakdown', sub: 'Company-wide · annualized', cards: runRateArrCards, categoryRows: runRateArrCategory, categoryHeaders: runRateCategoryHeaders, metricLabel: 'Ending ARR' })}
                    />
                    <KpiCard
                        tone="c" icon="📡" label={`Dec '${currentYear.slice(2)} AOP Run Rate % of ARR`} value={pctOfArrDecAop != null ? `${pctOfArrDecAop.toFixed(1)}%` : 'N/A'}
                        sub={`Dec AOP Run Rate vs. FY${currentYear.slice(2)} AOP Ending ARR`}
                        onClick={() => setPctDrill({ title: 'Run Rate % of ARR Breakdown', sub: 'Company-wide · annualized', cards: runRateArrCards, categoryRows: runRateArrCategory, categoryHeaders: runRateCategoryHeaders, metricLabel: 'Ending ARR' })}
                    />
                    <KpiCard
                        tone="c" icon="📡" label={`Dec '${currentYear.slice(2)} Fcst Run Rate % of ARR`} value={pctOfArrDecFcst != null ? `${pctOfArrDecFcst.toFixed(1)}%` : 'N/A'}
                        sub={`Dec Fcst Run Rate vs. FY${currentYear.slice(2)} Ending ARR`}
                        onClick={() => setPctDrill({ title: 'Run Rate % of ARR Breakdown', sub: 'Company-wide · annualized', cards: runRateArrCards, categoryRows: runRateArrCategory, categoryHeaders: runRateCategoryHeaders, metricLabel: 'Ending ARR' })}
                    />
                </div>
            </div>

            <div className="esd-section">
                <div className="esd-section-head">
                    <span className="esd-section-icon">📊</span>
                    <span className="esd-section-title">Cost Trends</span>
                    <span className="esd-section-note">Click any bar or scenario for the full detail</span>
                </div>
                <div className="esd-chart-grid">
                    <div className="esd-chart-card">
                        <div className="esd-chart-head"><span className="esd-chart-icon">📊</span>YTD Marketing Cost Comparison</div>
                        <div className="esd-chart-sub">Actuals vs. AOP &amp; PY {priorYear} — through {fmtMonthLabel(data.as_of)}</div>
                        <GroupedBarChart
                            groups={chart1Groups} series={chart1Series}
                            onGroupClick={month => openDrill(fmtMonthLabel(month), 'Actual/Forecast vs. AOP vs. Prior Year', [month])}
                        />
                    </div>
                    <div className="esd-chart-card">
                        <div className="esd-chart-head"><span className="esd-chart-icon">📈</span>Monthly Actuals vs. AOP vs. Prior Year</div>
                        <div className="esd-chart-sub">YTD actuals through {fmtMonthLabel(data.as_of)}, forecast for the rest of the year</div>
                        <StackedComboChart
                            groups={chart2Groups} stackSeries={chart2Stack} lineSeries={chart2Lines}
                            onGroupClick={month => openDrill(fmtMonthLabel(month), 'HC + Non-HC breakdown', [month])}
                        />
                    </div>
                </div>
            </div>

        </div>
    );
}

export { MarketingSummaryDashboard };
export default MarketingSummaryDashboard;
